import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Bot,
  Download,
  FileText,
  Globe,
  ImagePlus,
  Loader2,
  Menu,
  MessageSquare,
  Paperclip,
  ScrollText,
  Send,
  StopCircle,
  Trash2,
  Sparkles,
  Wand2,
  Undo2,
  Volume2,
  Film,
  Wrench,
  X,
  Pin,
  Copy,
  Square,
  Plus,
  MoreHorizontal,
  Code2,
  FolderTree,
  MousePointer2,
} from 'lucide-react';
import AiChatMarkdown from './AiChatMarkdown';
import AiChatRulesPanel from './AiChatRulesPanel';
import AiResearchProcess from './AiResearchProcess';
import SelectField from './SelectField';
import AgentWorkspace from './aiAgent/AgentWorkspace';
import { speakChatText, stopSpeech, subscribeSpeech, textForSpeech } from '../utils/chatSpeech';
import {
  flattenProjectFiles,
  getFileMentionAtCursor,
  filterProjectFiles,
  applyFileMention,
} from '../utils/agentFileMentions';

const MAX_ATTACH_FILES = 4;
const MAX_ATTACH_BYTES = 5 * 1024 * 1024;
const DEFAULT_CONTEXT_WINDOW = 8192;
const DEFAULT_SYSTEM_PROMPT_TOKENS = 420;
const DEFAULT_RESERVED_OUTPUT = 512;
const MODEL_STORAGE_KEY = 'odium_ai_model_v1';
const BACKEND_STORAGE_KEY = 'odium_ai_backend_v1';
const MODE_STORAGE_KEY = 'odium_ai_mode_v1';
const PROJECT_STORAGE_KEY = 'odium_ai_project_v1';
const CHAT_STORAGE_KEY = 'odium_ai_chat_v1';
const JOB_STORAGE_KEY = 'odium_ai_chat_job_v1';
const AGENT_CHAT_WIDTH_KEY = 'odium_ai_agent_chat_width_v1';
const AGENT_CHAT_WIDTH_MIN = 260;
const AGENT_CHAT_WIDTH_MAX = 720;
const AGENT_CHAT_WIDTH_DEFAULT = 320;

const clearStoredJobId = () => {
  try {
    sessionStorage.removeItem(JOB_STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
};

const storeJobId = (id) => {
  try {
    if (id) sessionStorage.setItem(JOB_STORAGE_KEY, String(id));
    else clearStoredJobId();
  } catch (_) {
    /* ignore */
  }
};

const readStoredJobId = () => {
  try {
    return String(sessionStorage.getItem(JOB_STORAGE_KEY) || '').trim();
  } catch (_) {
    return '';
  }
};

const formatDownloadSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const triggerBase64Download = (file) => {
  try {
    const raw = String(file?.data || '').replace(/^data:[^;]+;base64,/, '');
    const bin = atob(raw);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    const blob = new Blob([bytes], { type: file?.mimeType || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file?.name || 'download.txt';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch (_) {
    /* ignore */
  }
};

const BACKEND_OPTIONS = [
  { id: 'auto', label: 'Auto' },
  { id: 'heim', label: 'Heim' },
  { id: 'pi', label: 'Pi' },
  { id: 'server', label: 'Server' },
];

const friendlyStreamError = (raw) => {
  const msg = String(raw || '').trim();
  if (!msg) return 'Anfrage fehlgeschlagen';
  const lower = msg.toLowerCase();
  if (
    /\bterminated\b/.test(lower) ||
    lower.includes('body timeout') ||
    lower.includes('headers timeout')
  ) {
    return (
      'KI-Stream abgebrochen (terminated) — oft Pause während langem Thinking oder Ollama hat gestoppt. ' +
      'Bitte erneut senden; das Agent-Projekt bleibt erhalten.'
    );
  }
  if (
    lower.includes('fetch failed') ||
    lower.includes('econnrefused') ||
    lower.includes('econnreset') ||
    lower.includes('networkerror') ||
    lower === 'failed to fetch'
  ) {
    return (
      'KI-Verbindung abgebrochen — Tunnel/Ollama war während der Antwort weg. ' +
      'Reverse-SSH prüfen, dann dieselbe Nachricht erneut senden. Das Agent-Projekt bleibt erhalten.'
    );
  }
  return msg;
};

const friendlyBackendLabel = (id, backends = []) => {
  const key = String(id || '').trim().toLowerCase();
  if (!key || key === 'auto') return 'Auto';
  const hit = (Array.isArray(backends) ? backends : []).find((b) => b.id === key);
  if (hit?.label) return hit.label.replace(/-KI$/i, '');
  if (key === 'heim') return 'Heim';
  if (key === 'pi') return 'Pi';
  if (key === 'server') return 'Server';
  return key;
};

const friendlyModelLabel = (name) => {
  const n = String(name || '').trim();
  if (!n) return 'Modell';
  if (/Claude-Agent/i.test(n)) return 'Claude-Agent 9B';
  if (/HauhauCS|uncensored|hauhau-qwen/i.test(n)) return 'Qwen 3.5 Uncensored 9B';
  if (/gemma4:12b/i.test(n)) return 'Gemma 4 12B';
  if (/qwen3\.5:9b/i.test(n)) return 'Qwen 3.5 9B';
  if (/qwen3\.5:4b/i.test(n)) return 'Qwen 3.5 4B';
  if (/qwen3\.5:2b/i.test(n)) return 'Qwen 3.5 2B';
  if (/qwen3:8b/i.test(n)) return 'Qwen 3 8B';
  if (/qwen3-coder:30b/i.test(n)) return 'Qwen3 Coder 30B';
  const short = n.includes('/') ? n.split('/').pop() : n;
  return short.length > 28 ? `${short.slice(0, 26)}…` : short;
};

/** Gleiche Modelle unter verschiedenen Ollama-Namen zusammenfassen. */
const modelFamilyKey = (name) => {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return '';
  if (/claude-agent/.test(n)) return 'claude-agent';
  if (/hauhau|uncensored/.test(n)) return 'qwen35-uncensored';
  if (/gemma4:12b/.test(n)) return 'gemma4-12b';
  return n;
};

const modelPreferenceScore = (name) => {
  const n = String(name || '');
  // Kurze Aliase bevorzugen vor hf.co/... Pfaden
  if (/^qwen3\.5-uncensored:9b$/i.test(n)) return 100;
  if (/^hauhau-qwen35-uncensored:9b$/i.test(n)) return 80;
  if (/^Tesleum\/Claude-Agent/i.test(n)) return 100;
  if (/^gemma4:12b$/i.test(n)) return 100;
  if (n.startsWith('hf.co/')) return 10;
  return 50;
};

const dedupeModelOptions = (models, preferred = '') => {
  const list = (Array.isArray(models) ? models : []).map((m) => String(m || '').trim()).filter(Boolean);
  const byFamily = new Map();
  for (const name of list) {
    const key = modelFamilyKey(name);
    const prev = byFamily.get(key);
    if (!prev) {
      byFamily.set(key, name);
      continue;
    }
    const preferThis =
      name === preferred ||
      (prev !== preferred && modelPreferenceScore(name) > modelPreferenceScore(prev));
    if (preferThis) byFamily.set(key, name);
  }
  return Array.from(byFamily.values());
};

const PHASE_META = {
  prepare: { label: 'Vorbereitung', short: 'Start' },
  files: { label: 'Anhänge lesen', short: 'Anhänge' },
  image: { label: 'Bild erzeugen', short: 'Bild' },
  video: { label: 'Video erzeugen', short: 'Video' },
  sound: { label: 'Sound erzeugen', short: 'Sound' },
  pdf: { label: 'PDF erzeugen', short: 'PDF' },
  search: { label: 'Websuche', short: 'Web' },
  agent: { label: 'Agent arbeitet', short: 'Agent' },
  think: { label: 'Nachdenken', short: 'Denken' },
  write: { label: 'Antwort schreiben', short: 'Schreiben' },
};

const formatElapsed = (ms) => {
  const n = Math.max(0, Number(ms) || 0);
  if (n < 1000) return `${Math.round(n)} ms`;
  const s = n / 1000;
  if (s < 60) return `${s.toFixed(1).replace('.', ',')} s`;
  const m = Math.floor(s / 60);
  const rem = Math.floor(s % 60);
  return `${m}:${String(rem).padStart(2, '0')}`;
};

const formatThoughtDuration = (ms) => {
  if (ms == null || !Number.isFinite(Number(ms))) return '';
  return `${formatElapsed(ms)} nachgedacht`;
};

const formatMessageTime = (ts) => {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return '';
  try {
    return new Date(n).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_) {
    return '';
  }
};

const phaseLabel = (phase) => PHASE_META[phase]?.label || 'Arbeitet…';

/** Chat-Phasen-Leiste — nur Chat-Modus, nicht Agent (dort zählen Tool-Schritte). */
const PhasePipelineStrip = ({ pipeline, phase, phaseMessage, elapsedLabel }) => {
  const steps = Array.isArray(pipeline) ? pipeline.filter(Boolean) : [];
  if (!steps.length) return null;
  const currentIdx = steps.indexOf(phase);
  const focusIdx = currentIdx >= 0 ? currentIdx : 0;
  return (
    <div className="rounded-xl border border-neutral-200/90 bg-white/70 px-3 py-2 dark:border-neutral-800 dark:bg-neutral-950/50">
      <div className="flex items-center justify-between gap-2 mb-1.5 min-w-0">
        <p className="text-[11px] font-medium text-neutral-700 dark:text-neutral-200 truncate min-w-0">
          {phaseMessage || phaseLabel(phase)}
        </p>
        {elapsedLabel ? (
          <span
            className="text-[11px] tabular-nums text-emerald-600 dark:text-emerald-400 font-medium flex-shrink-0"
            title="Bisherige Nachdenkzeit"
          >
            {elapsedLabel}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {steps.map((step, stepIdx) => {
          const done = stepIdx < focusIdx;
          const active = stepIdx === focusIdx;
          return (
            <React.Fragment key={step}>
              {stepIdx > 0 ? (
                <span className="text-neutral-300 dark:text-neutral-600 text-[10px]" aria-hidden>
                  →
                </span>
              ) : null}
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border transition-colors ${
                  active
                    ? 'border-emerald-500/50 bg-emerald-500/20 text-emerald-800 shadow-[0_0_0_1px_rgba(16,185,129,0.25)] dark:text-emerald-200 dark:border-emerald-400/50 dark:bg-emerald-500/25'
                    : done
                      ? 'border-neutral-200 bg-neutral-100 text-neutral-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-400'
                      : 'border-neutral-200/80 bg-transparent text-neutral-400 dark:border-neutral-800 dark:text-neutral-500'
                }`}
                aria-current={active ? 'step' : undefined}
              >
                {active ? <Loader2 className="w-2.5 h-2.5 mr-1 animate-spin" /> : null}
                {PHASE_META[step]?.short || step}
              </span>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

/** Thinking-Delta in Nachricht + aktiven Agent-Thinking-Schritt spiegeln */
const applyThinkDeltaToMessage = (m, delta) => {
  const nextThinking = `${m.thinkingText || ''}${delta}`;
  const steps = Array.isArray(m.agentSteps) ? m.agentSteps : null;
  let agentSteps = steps;
  if (steps && steps.length) {
    let thinkIdx = -1;
    for (let i = steps.length - 1; i >= 0; i -= 1) {
      const s = steps[i];
      if (s && (s.status === 'thinking' || s.status === 'active') && !s.tool) {
        thinkIdx = i;
        break;
      }
    }
    if (thinkIdx >= 0) {
      const clipped = nextThinking.slice(-8000);
      agentSteps = steps.map((s, i) =>
        i === thinkIdx
          ? {
              ...s,
              preview: clipped,
              details: { ...(s.details || {}), thought: clipped },
            }
          : s
      );
    }
  }
  return {
    ...m,
    thinkingText: nextThinking,
    agentSteps: agentSteps || m.agentSteps,
    streaming: true,
  };
};

const isFileMutateTool = (tool) =>
  /^(write_file|datei_schreiben|edit_file|datei_aendern|datei_ändern|search_replace|patch)$/i.test(
    String(tool || '')
  );

const stepFilePath = (s) =>
  String(s?.details?.path || s?.args?.path || s?.path || '')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .trim();

/** Agent-Step mergen — Write/Edit per Pfad matchen (Streaming-Index ≠ Server-Index). */
const mergeAgentStepEntry = (prevSteps, step) => {
  const prev = Array.isArray(prevSteps) ? prevSteps : [];
  const idx = Number.isFinite(Number(step.index)) ? Number(step.index) : prev.length;
  let next = [...prev];
  // Thinking nicht automatisch schließen, wenn ein Tool startet —
  // Trennung läuft im Thinking-Text; Schritt bleibt sichtbar offen bis Server ihn beendet.
  const tool = String(step.tool || '');
  const path = stepFilePath(step);
  let existingIdx = next.findIndex(
    (s) => Number(s.index) === idx && String(s.tool || '') === tool
  );
  // Live-Stream legt oft schon einen write_file-Schritt mit anderem index an
  if (existingIdx < 0 && isFileMutateTool(tool)) {
    for (let i = next.length - 1; i >= 0; i -= 1) {
      if (!isFileMutateTool(next[i].tool)) continue;
      const p = stepFilePath(next[i]);
      if (path && p && p === path) {
        existingIdx = i;
        break;
      }
      if (!path && (next[i].status === 'running' || next[i].status === 'thinking')) {
        existingIdx = i;
        break;
      }
    }
  }
  const entry = {
    index: idx,
    tool,
    status: step.status || 'running',
    label: step.label || '',
    preview: step.preview || '',
    args: step.args || null,
    details: step.details || null,
  };
  if (existingIdx >= 0) {
    const prevDetails = next[existingIdx].details || {};
    const nextDetails = { ...prevDetails, ...(entry.details || {}) };
    // undefined darf einen bereits gestreamten Diff nicht löschen
    if (entry.details && entry.details.diff == null && prevDetails.diff) {
      nextDetails.diff = prevDetails.diff;
    }
    if (entry.details && entry.details.revisionId == null && prevDetails.revisionId) {
      nextDetails.revisionId = prevDetails.revisionId;
    }
    if (entry.details && entry.details.created == null && prevDetails.created != null) {
      nextDetails.created = prevDetails.created;
    }
    next[existingIdx] = {
      ...next[existingIdx],
      ...entry,
      index: next[existingIdx].index ?? entry.index,
      details: nextDetails,
      preview: entry.preview || next[existingIdx].preview || '',
      label: entry.label || next[existingIdx].label || '',
    };
  } else {
    next.push(entry);
  }
  return next;
};

/** Grobe Token-Schätzung (DE/EN + Code), analog ~3,2 Zeichen/Token. */
const estimateTokens = (text) => {
  const s = String(text || '');
  if (!s) return 0;
  return Math.max(1, Math.ceil(s.length / 3.2));
};

const formatTokenCount = (n) => {
  const v = Math.max(0, Math.round(Number(n) || 0));
  if (v < 1000) return String(v);
  if (v < 10000) return `${(v / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  if (v < 1000000) return `${Math.round(v / 1000)}K`;
  return `${(v / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
};

const estimateAttachmentTokens = (file) => {
  const size = Number(file?.size) || 0;
  if (size > 0) return Math.min(8000, Math.ceil(size / 4));
  const dataLen = String(file?.data || '').length;
  return Math.min(8000, Math.ceil(dataLen / 6));
};

const estimateChatContextTokens = ({
  messages = [],
  input = '',
  attachments = [],
  systemPromptTokens = DEFAULT_SYSTEM_PROMPT_TOKENS,
}) => {
  let total = Math.max(0, Number(systemPromptTokens) || 0);
  const history = (messages || [])
    .filter((m) => m && !m.isError && (m.role === 'user' || m.role === 'assistant') && m.content)
    .slice(-24);
  for (const m of history) {
    total += estimateTokens(String(m.content || '').slice(0, 6000));
  }
  if (input) total += estimateTokens(input);
  for (const file of attachments || []) {
    total += estimateAttachmentTokens(file);
  }
  return total;
};

const ContextWindowMeter = ({ used, max, percent, compact = false }) => {
  const pct = Math.max(0, Math.min(100, Number(percent) || 0));
  const size = compact ? 16 : 18;
  const stroke = compact ? 2 : 2.25;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  const tone =
    pct >= 90
      ? { ring: '#f43f5e', text: 'text-rose-600 dark:text-rose-300' }
      : pct >= 70
        ? { ring: '#f59e0b', text: 'text-amber-600 dark:text-amber-300' }
        : { ring: '#10b981', text: 'text-neutral-600 dark:text-neutral-300' };

  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-950 ${
        compact ? 'p-1.5' : 'px-2 py-1'
      }`}
      title={`Context Window: ${Math.round(used).toLocaleString('de-DE')} / ${Math.round(max).toLocaleString('de-DE')} Tokens (${Math.round(pct)} %)`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0 -rotate-90" aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          className="text-neutral-200 dark:text-neutral-700"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={tone.ring}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
          className="transition-[stroke-dasharray] duration-300"
        />
      </svg>
      {!compact ? (
        <span className={`text-[10px] sm:text-[11px] font-medium tabular-nums leading-none ${tone.text}`}>
          {formatTokenCount(used)}
          <span className="text-neutral-400 dark:text-neutral-500"> / </span>
          {formatTokenCount(max)}
        </span>
      ) : null}
    </div>
  );
};

const isImageAttachment = (file) => {
  const mime = String(file?.mimeType || '').toLowerCase();
  const name = String(file?.name || '').toLowerCase();
  return mime.startsWith('image/') || /\.(png|jpe?g|webp|gif|bmp|tiff?)$/i.test(name);
};

/** Fake-Bild-Downloads (.jpg als Text) aus der Download-Liste filtern */
const filterRealDownloads = (files) =>
  (Array.isArray(files) ? files : []).filter((f) => {
    const name = String(f?.name || '').trim();
    if (!name) return false;
    return !/\.(png|jpe?g|webp|gif|bmp|svg|ico|avif|heic|tiff?)$/i.test(name);
  });

const notifyMediaLibraryChanged = (detail = {}) => {
  try {
    window.dispatchEvent(new CustomEvent('odium:media-changed', { detail }));
  } catch (_) {
    /* ignore */
  }
};

const attachmentPreviewUrl = (file) => {
  if (!file?.data) return '';
  if (String(file.data).startsWith('data:')) return file.data;
  const mime = file.mimeType || 'application/octet-stream';
  return `data:${mime};base64,${file.data}`;
};

const readFileAsAttachment = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const data = result.includes(',') ? result.split(',')[1] : result;
      resolve({
        name: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
        data,
      });
    };
    reader.onerror = () => reject(new Error(`Datei „${file.name}“ konnte nicht gelesen werden`));
    reader.readAsDataURL(file);
  });

const AiChat = () => {
  const [status, setStatus] = useState({
    loading: true,
    online: false,
    models: [],
    defaultModel: 'qwen2.5:1.5b',
    webSearchEnabled: true,
    contextWindow: DEFAULT_CONTEXT_WINDOW,
    reservedOutputTokens: DEFAULT_RESERVED_OUTPUT,
    systemPromptTokens: DEFAULT_SYSTEM_PROMPT_TOKENS,
    backend: null,
    backends: [],
    error: '',
  });
  const [chatBackend, setChatBackend] = useState(null);
  const [selectedBackend, setSelectedBackend] = useState(() => {
    try {
      const stored = String(localStorage.getItem(BACKEND_STORAGE_KEY) || 'auto').trim().toLowerCase();
      return BACKEND_OPTIONS.some((b) => b.id === stored) ? stored : 'auto';
    } catch (_) {
      return 'auto';
    }
  });
  const [selectedModel, setSelectedModel] = useState(() => {
    try {
      return String(localStorage.getItem(MODEL_STORAGE_KEY) || '').trim();
    } catch (_) {
      return '';
    }
  });
  const [chatMode, setChatMode] = useState(() => {
    try {
      const stored = String(localStorage.getItem(MODE_STORAGE_KEY) || 'chat').trim().toLowerCase();
      return stored === 'agent' ? 'agent' : 'chat';
    } catch (_) {
      return 'chat';
    }
  });
  const [projectId, setProjectId] = useState(() => {
    try {
      return String(localStorage.getItem(PROJECT_STORAGE_KEY) || '').trim();
    } catch (_) {
      return '';
    }
  });
  const [activeFilePath, setActiveFilePath] = useState('');
  /** Datei, die der Agent gerade streamt — unabhängig vom geöffneten Editor-Tab */
  const [agentWritingPath, setAgentWritingPath] = useState('');
  const [pinnedPaths, setPinnedPaths] = useState([]);
  const [codeRefs, setCodeRefs] = useState([]);
  const [workspaceTick, setWorkspaceTick] = useState(0);
  const [previewTick, setPreviewTick] = useState(0);
  const [agentChatWidth, setAgentChatWidth] = useState(() => {
    try {
      const n = parseInt(localStorage.getItem(AGENT_CHAT_WIDTH_KEY) || '', 10);
      if (Number.isFinite(n) && n >= AGENT_CHAT_WIDTH_MIN && n <= AGENT_CHAT_WIDTH_MAX) return n;
    } catch (_) {
      /* ignore */
    }
    return AGENT_CHAT_WIDTH_DEFAULT;
  });
  const [showRulesPanel, setShowRulesPanel] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const raw = sessionStorage.getItem(CHAT_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? parsed.filter(
            (m) =>
              m &&
              (m.role === 'user' || m.role === 'assistant') &&
              (m.content ||
                m.pendingJob ||
                m.streaming ||
                (Array.isArray(m.agentSteps) && m.agentSteps.length) ||
                (Array.isArray(m.images) && m.images.length) ||
                (Array.isArray(m.videos) && m.videos.length) ||
                (Array.isArray(m.audios) && m.audios.length) ||
                (Array.isArray(m.pdfs) && m.pdfs.length))
          )
        : [];
    } catch (_) {
      return [];
    }
  });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [rewritingPrompt, setRewritingPrompt] = useState(false);
  const [promptRewriteUndo, setPromptRewriteUndo] = useState('');
  const inputBeforeRewriteRef = useRef('');
  const [webSearch, setWebSearch] = useState(false);
  const [phase, setPhase] = useState(''); // prepare|files|search|think|write
  const [phaseMessage, setPhaseMessage] = useState('');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [pipeline, setPipeline] = useState(['prepare', 'think', 'write']);
  const [attachments, setAttachments] = useState([]);
  const [error, setError] = useState('');
  const [agentAnswerDraft, setAgentAnswerDraft] = useState('');
  const [agentAnswerBusy, setAgentAnswerBusy] = useState(false);
  const [speakingKey, setSpeakingKey] = useState(null);
  const [speakBusyKey, setSpeakBusyKey] = useState(null);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [attachDragOver, setAttachDragOver] = useState(false);
  const [headerMoreOpen, setHeaderMoreOpen] = useState(false);
  /** Mobile Agent: files | work | chat */
  const [agentMobileTab, setAgentMobileTab] = useState('chat');
  const [isNarrow, setIsNarrow] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  /** Projektdateien für @-Vervollständigung im Agent-Chat */
  const [projectFileIndex, setProjectFileIndex] = useState([]);
  const [fileSuggest, setFileSuggest] = useState(null);
  /** Letzte echte Token-Zählung von Ollama (nach Antwort). */
  const [lastUsage, setLastUsage] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const attachMenuRef = useRef(null);
  const attachDragDepthRef = useRef(0);
  const headerMoreRef = useRef(null);
  const abortRef = useRef(null);
  const activeJobIdRef = useRef(readStoredJobId());
  const resumeStartedRef = useRef(false);
  const scrollContainerRef = useRef(null);
  const rootRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const ignoreScrollRef = useRef(false);
  const thinkStartedAtRef = useRef(0);
  const agentLayoutRef = useRef(null);
  const agentChatWidthRef = useRef(agentChatWidth);
  const resizingAgentChatRef = useRef(false);
  /** true = Agent darf den Editor-Tab wechseln; false sobald der Nutzer woanders hin klickt */
  const followAgentFileRef = useRef(true);
  const agentWritingPathRef = useRef('');
  const activeFilePathRef = useRef(activeFilePath);
  activeFilePathRef.current = activeFilePath;

  const normalizeAgentPath = (path) => String(path || '').replace(/\\/g, '/').replace(/^\/+/, '').trim();

  /** Nutzer navigiert manuell — Follow nur beibehalten, wenn er die Agent-Datei öffnet */
  const selectUserFile = (path) => {
    const next = normalizeAgentPath(path);
    const writing = normalizeAgentPath(agentWritingPathRef.current);
    if (writing && next && next !== writing) {
      followAgentFileRef.current = false;
    } else if (writing && next === writing) {
      followAgentFileRef.current = true;
    }
    setActiveFilePath(next);
    return next;
  };

  const openFileWithDiff = (path, { diff, revisionId, content } = {}) => {
    const next = selectUserFile(path);
    if (!next) return;
    try {
      window.dispatchEvent(
        new CustomEvent('odium:agent-workspace', { detail: { tab: 'editor' } })
      );
    } catch (_) {
      /* ignore */
    }
    if (diff || content) {
      const fire = () => {
        try {
          window.dispatchEvent(
            new CustomEvent('odium:agent-file-live', {
              detail: {
                projectId,
                path: next,
                content: content != null ? String(content) : undefined,
                diff,
                revisionId: revisionId || undefined,
                done: true,
                stage: 'done',
              },
            })
          );
        } catch (_) {
          /* ignore */
        }
      };
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => setTimeout(fire, 50));
      } else {
        setTimeout(fire, 50);
      }
    }
  };

  const markAgentRevisionRejected = useCallback((revisionId) => {
    const rid = String(revisionId || '').trim();
    if (!rid) return;
    setMessages((prev) =>
      prev.map((m) => {
        if (!Array.isArray(m.agentSteps) || !m.agentSteps.length) return m;
        let touched = false;
        const agentSteps = m.agentSteps.map((s) => {
          if (String(s.details?.revisionId || '') !== rid) return s;
          touched = true;
          return {
            ...s,
            status: 'error',
            details: {
              ...(s.details || {}),
              rejected: true,
              diff: null,
            },
          };
        });
        return touched ? { ...m, agentSteps } : m;
      })
    );
  }, []);

  /** Agent schreibt: Datei sofort im Editor zeigen (sichtbares Tippen). */
  const revealAgentWritingFile = (path, { done = false } = {}) => {
    const next = normalizeAgentPath(path);
    if (!next) return;
    if (done) {
      if (normalizeAgentPath(agentWritingPathRef.current) === next) {
        agentWritingPathRef.current = '';
        setAgentWritingPath('');
      }
      return;
    }
    agentWritingPathRef.current = next;
    setAgentWritingPath(next);
    // Immer der schreibenden Datei folgen — sonst sieht man lange nichts
    followAgentFileRef.current = true;
    setActiveFilePath(next);
    try {
      window.dispatchEvent(
        new CustomEvent('odium:agent-workspace', { detail: { tab: 'editor' } })
      );
    } catch (_) {
      /* ignore */
    }
    if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches) {
      setAgentMobileTab('work');
    }
  };

  useEffect(() => {
    agentChatWidthRef.current = agentChatWidth;
  }, [agentChatWidth]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => setIsNarrow(!!mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    return subscribeSpeech(({ speakingKey: key }) => {
      setSpeakingKey(key || null);
    });
  }, []);

  useEffect(() => {
    const onFocus = (ev) => {
      const d = ev?.detail || {};
      if (!isNarrow) return;
      if (d.tab === 'live' || d.tab === 'editor' || d.terminal || d.refreshPreview) {
        setAgentMobileTab('work');
      }
    };
    window.addEventListener('odium:agent-workspace', onFocus);
    return () => window.removeEventListener('odium:agent-workspace', onFocus);
  }, [isNarrow]);

  useEffect(() => {
    return () => {
      stopSpeech();
    };
  }, []);

  useEffect(() => {
    if (!attachMenuOpen) return undefined;
    const onDoc = (e) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target)) {
        setAttachMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setAttachMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [attachMenuOpen]);

  useEffect(() => {
    if (!headerMoreOpen) return undefined;
    const onDoc = (e) => {
      if (headerMoreRef.current && !headerMoreRef.current.contains(e.target)) {
        setHeaderMoreOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setHeaderMoreOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [headerMoreOpen]);

  const authHeaders = () => {
    const token = localStorage.getItem('authToken') || '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Agent-Chat: Projektdateien für @-Vervollständigung laden
  useEffect(() => {
    if (chatMode !== 'agent' || !projectId) {
      setProjectFileIndex([]);
      setFileSuggest(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/api/ai/projects/${encodeURIComponent(projectId)}/tree`, {
          headers: authHeaders(),
        });
        if (cancelled) return;
        setProjectFileIndex(flattenProjectFiles(res.data?.tree || []));
      } catch (_) {
        if (!cancelled) setProjectFileIndex([]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMode, projectId, workspaceTick]);

  const updateFileSuggestFromInput = (nextValue, cursorPos) => {
    if (chatMode !== 'agent' || !projectId || !projectFileIndex.length) {
      setFileSuggest(null);
      return;
    }
    const mention = getFileMentionAtCursor(nextValue, cursorPos);
    if (!mention) {
      setFileSuggest(null);
      return;
    }
    // Bei @ immer vorschlagen (auch leere Query = Top-Dateien); sonst ab 2 Zeichen
    if (!mention.triggeredByAt && mention.query.length < 2) {
      setFileSuggest(null);
      return;
    }
    const items = filterProjectFiles(projectFileIndex, mention.query, 12);
    // Ohne @: nur anzeigen wenn klarer Dateitreffer (Name/Pfad), nicht jedes Fuzzy-Wort
    const strong = mention.triggeredByAt
      ? items
      : items.filter((f) => f.score >= 300);
    if (!strong.length) {
      setFileSuggest(null);
      return;
    }
    setFileSuggest({
      open: true,
      query: mention.query,
      start: mention.start,
      end: mention.end,
      triggeredByAt: mention.triggeredByAt,
      items: strong,
      index: 0,
    });
  };

  const acceptFileSuggestion = (file) => {
    const ta = inputRef.current;
    const cursor = ta ? ta.selectionStart : input.length;
    const mention =
      fileSuggest && fileSuggest.open
        ? {
            start: fileSuggest.start,
            end: fileSuggest.end,
            query: fileSuggest.query,
            triggeredByAt: fileSuggest.triggeredByAt,
          }
        : getFileMentionAtCursor(input, cursor);
    if (!mention || !file?.path) {
      setFileSuggest(null);
      return;
    }
    const { text, cursor: nextCursor } = applyFileMention(input, mention, file.path);
    setInput(text);
    setFileSuggest(null);
    // Auch als Bezug anheften (wie Pin im Editor)
    const path = String(file.path || '')
      .replace(/\\/g, '/')
      .replace(/^\/+/, '')
      .trim();
    if (path) {
      setPinnedPaths((prev) => {
        if (prev.includes(path)) return prev;
        return [...prev, path].slice(0, 8);
      });
    }
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      try {
        el.setSelectionRange(nextCursor, nextCursor);
      } catch (_) {
        /* ignore */
      }
    });
  };

  const copyMessageText = async (text) => {
    const t = String(text || '').trim();
    if (!t) return;
    try {
      await navigator.clipboard.writeText(t);
    } catch (_) {
      try {
        const ta = document.createElement('textarea');
        ta.value = t;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      } catch (__) {
        /* ignore */
      }
    }
  };

  const toggleReadAloud = async (msg, msgIdx) => {
    const key = `msg-${msgIdx}-${msg.createdAt || 0}`;
    if (speakingKey === key || speakBusyKey === key) {
      stopSpeech();
      setSpeakBusyKey(null);
      return;
    }
    const text = textForSpeech(msg.content);
    if (!text) return;
    setSpeakBusyKey(key);
    setError('');
    try {
      await speakChatText(msg.content, {
        key,
        authHeaders: authHeaders(),
      });
    } catch (err) {
      setError(err.message || 'Vorlesen fehlgeschlagen');
    } finally {
      setSpeakBusyKey(null);
    }
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!resizingAgentChatRef.current) return;
      const root = agentLayoutRef.current;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      const next = Math.round(rect.right - e.clientX);
      const maxForViewport = Math.max(
        AGENT_CHAT_WIDTH_MIN,
        Math.min(AGENT_CHAT_WIDTH_MAX, Math.floor(rect.width * 0.72))
      );
      const clamped = Math.max(AGENT_CHAT_WIDTH_MIN, Math.min(maxForViewport, next));
      setAgentChatWidth(clamped);
      agentChatWidthRef.current = clamped;
    };
    const onUp = () => {
      if (!resizingAgentChatRef.current) return;
      resizingAgentChatRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try {
        localStorage.setItem(AGENT_CHAT_WIDTH_KEY, String(agentChatWidthRef.current));
      } catch (_) {
        /* ignore */
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  // Mobile: Höhe an visuelle Viewport anpassen (Tastatur öffnen/schließen)
  useEffect(() => {
    const root = rootRef.current;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!root || !vv) return undefined;

    const sync = () => {
      const top = root.getBoundingClientRect().top;
      const available = Math.max(220, vv.height - Math.max(0, top - (vv.offsetTop || 0)));
      root.style.height = `${Math.round(available)}px`;
    };

    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    window.addEventListener('orientationchange', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      window.removeEventListener('orientationchange', sync);
      root.style.height = '';
    };
  }, []);

  // Nutzer will hochscrollen → Autoscroll aus; nahe am Boden wieder an
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return undefined;

    const nearBottom = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      return distance <= 96;
    };

    const onWheel = (e) => {
      if (e.deltaY < 0) {
        stickToBottomRef.current = false;
        return;
      }
      if (nearBottom()) stickToBottomRef.current = true;
    };

    let touchY = null;
    const onTouchStart = (e) => {
      touchY = e.touches?.[0]?.clientY ?? null;
    };
    const onTouchMove = (e) => {
      const y = e.touches?.[0]?.clientY;
      if (touchY == null || y == null) return;
      if (y > touchY + 6) stickToBottomRef.current = false; // Finger nach unten = Inhalt nach oben
      else if (y < touchY - 6 && nearBottom()) stickToBottomRef.current = true;
      touchY = y;
    };

    const onScroll = () => {
      if (ignoreScrollRef.current) return;
      stickToBottomRef.current = nearBottom();
    };

    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    try {
      const persist = messages.slice(-40).map((m) => {
        if (m.streaming || m.pendingJob) {
          return {
            role: m.role,
            content: m.content || '',
            pendingJob: true,
            jobId: m.jobId || activeJobIdRef.current || undefined,
            createdAt: m.createdAt,
            model: m.model,
            modelLabel: m.modelLabel,
            chatMode: m.chatMode,
            search: m.search || null,
            images: m.images || [],
            videos: m.videos || [],
            audios: m.audios || [],
            pdfs: m.pdfs || [],
            // Agent-Schritte behalten, sonst nach Reload nur noch Pipeline-Doppelungen
            agentSteps: Array.isArray(m.agentSteps) ? m.agentSteps.slice(-40) : [],
            thinkingText: m.thinkingText ? String(m.thinkingText).slice(-8000) : '',
            agentQuestion: m.agentQuestion || undefined,
          };
        }
        return {
          ...m,
          downloads: (m.downloads || []).map(({ data, ...meta }) => meta),
        };
      });
      sessionStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(persist));
    } catch (_) {
      // ignore quota
    }
  }, [messages]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/ai/status', {
          params: {
            backend: selectedBackend || 'auto',
            // Bei Offline/Heim-Problemen Cache umgehen
            ...(String(selectedBackend || '') === 'heim' || String(selectedBackend || '') === 'pi'
              ? { refresh: '1' }
              : {}),
          },
          validateStatus: (s) => s < 600,
        });
        if (cancelled) return;
        const backends = Array.isArray(res.data?.backends) ? res.data.backends : [];
        const enabled = res.data?.webSearchEnabled !== false;
        const modelsRaw = Array.isArray(res.data?.models) ? res.data.models.filter(Boolean) : [];
        const defaultModel = res.data?.defaultModel || 'qwen2.5:1.5b';
        const models = dedupeModelOptions(modelsRaw, defaultModel);
        const online = !!res.data?.online;
        setStatus({
          loading: false,
          online,
          models,
          defaultModel,
          webSearchEnabled: enabled,
          contextWindow: Number(res.data?.contextWindow) || DEFAULT_CONTEXT_WINDOW,
          reservedOutputTokens: Number(res.data?.reservedOutputTokens) || DEFAULT_RESERVED_OUTPUT,
          systemPromptTokens: Number(res.data?.systemPromptTokens) || DEFAULT_SYSTEM_PROMPT_TOKENS,
          backend: res.data?.backend || null,
          backends,
          error: online ? '' : res.data?.error || 'KI offline',
        });
        if (res.data?.backend) setChatBackend(res.data.backend);
        setSelectedModel((prev) => {
          let stored = prev;
          try {
            stored = String(localStorage.getItem(MODEL_STORAGE_KEY) || prev || '').trim();
          } catch (_) {
            /* ignore */
          }
          const storedCanon =
            stored && models.includes(stored)
              ? stored
              : models.find((m) => modelFamilyKey(m) === modelFamilyKey(stored));
          const next =
            storedCanon ||
            (models.includes(defaultModel) && defaultModel) ||
            models[0] ||
            defaultModel;
          try {
            localStorage.setItem(MODEL_STORAGE_KEY, next);
          } catch (_) {
            /* ignore */
          }
          return next;
        });
      } catch (err) {
        if (cancelled) return;
        setStatus({
          loading: false,
          online: false,
          models: [],
          defaultModel: 'qwen2.5:1.5b',
          webSearchEnabled: true,
          contextWindow: DEFAULT_CONTEXT_WINDOW,
          reservedOutputTokens: DEFAULT_RESERVED_OUTPUT,
          systemPromptTokens: DEFAULT_SYSTEM_PROMPT_TOKENS,
          backend: null,
          backends: [],
          error: err.response?.data?.error || err.message || 'KI nicht erreichbar',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBackend]);

  const activeModel = (() => {
    const raw = selectedModel || status.defaultModel;
    if (status.models.includes(raw)) return raw;
    const canon = status.models.find((m) => modelFamilyKey(m) === modelFamilyKey(raw));
    return canon || raw;
  })();
  const scrollToBottom = (behavior = 'auto') => {
    if (!stickToBottomRef.current) return;
    const el = scrollContainerRef.current;
    if (!el) {
      bottomRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      return;
    }
    ignoreScrollRef.current = true;
    // Immer instant während Streaming — smooth kämpft gegen manuelles Scrollen
    el.scrollTop = el.scrollHeight;
    window.requestAnimationFrame(() => {
      ignoreScrollRef.current = false;
    });
  };

  useEffect(() => {
    if (!stickToBottomRef.current) return;
    scrollToBottom();
    // Nur Nachrichteninhalt — nicht phase/Websuche-Status (sonst Ziehen trotz Hochscrollen)
  }, [messages, sending]);

  useEffect(() => {
    const onSelecting = (e) => {
      if (e?.detail?.active) {
        // Während Textmarkierung im Thinking nicht auto-scrollen (sonst springt die Auswahl)
        stickToBottomRef.current = false;
      }
    };
    window.addEventListener('odium:ai-text-selecting', onSelecting);
    return () => window.removeEventListener('odium:ai-text-selecting', onSelecting);
  }, []);

  useEffect(() => {
    if (!sending) return undefined;
    thinkStartedAtRef.current = Date.now();
    setElapsedMs(0);
    const id = window.setInterval(() => {
      setElapsedMs(Date.now() - thinkStartedAtRef.current);
    }, 100);
    return () => window.clearInterval(id);
  }, [sending]);

  const contextWindow = Math.max(1024, Number(status.contextWindow) || DEFAULT_CONTEXT_WINDOW);
  const estimatedUsed = estimateChatContextTokens({
    messages,
    input,
    attachments,
    systemPromptTokens: status.systemPromptTokens || DEFAULT_SYSTEM_PROMPT_TOKENS,
  });
  const contextUsed =
    lastUsage?.totalTokens > 0 && !input.trim() && !attachments.length && !sending
      ? lastUsage.totalTokens
      : estimatedUsed;
  const contextPercent = Math.min(100, (contextUsed / contextWindow) * 100);

  const ensurePipelineStep = (step) => {
    if (!step) return;
    setPipeline((prev) => {
      if (prev.includes(step)) return prev;
      const order = ['prepare', 'files', 'image', 'video', 'sound', 'pdf', 'search', 'think', 'write'];
      const next = [...prev.filter((s) => s !== 'write'), step]
        .filter((s, i, arr) => arr.indexOf(s) === i)
        .sort((a, b) => order.indexOf(a) - order.indexOf(b));
      if (!next.includes('write')) next.push('write');
      return next;
    });
  };

  useEffect(() => {
    // Nur lokale SSE-Verbindung trennen — Job auf dem Server weiterlaufen lassen (Reload)
    return () => {
      try {
        abortRef.current?.abort();
      } catch (_) {
        /* ignore */
      }
    };
  }, []);

  const cancelGeneration = () => {
    const jobId = activeJobIdRef.current || readStoredJobId();
    try {
      abortRef.current?.abort();
    } catch (_) {
      /* ignore */
    }
    if (jobId) {
      clearStoredJobId();
      activeJobIdRef.current = '';
      const token = localStorage.getItem('authToken') || '';
      fetch(`/api/ai/chat/jobs/${encodeURIComponent(jobId)}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).catch(() => {});
    }
  };

  const addFiles = async (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setError('');
    const room = MAX_ATTACH_FILES - attachments.length;
    if (room <= 0) {
      setError(`Maximal ${MAX_ATTACH_FILES} Dateien`);
      return;
    }
    const next = [];
    for (const file of incoming.slice(0, room)) {
      if (file.size > MAX_ATTACH_BYTES) {
        setError(`„${file.name}“ ist zu groß (max. 5 MB)`);
        continue;
      }
      try {
        next.push(await readFileAsAttachment(file));
      } catch (err) {
        setError(err.message || 'Datei konnte nicht gelesen werden');
      }
    }
    if (next.length) setAttachments((prev) => [...prev, ...next].slice(0, MAX_ATTACH_FILES));
  };

  const hasOsFileDrag = (dataTransfer) =>
    Array.from(dataTransfer?.types || []).includes('Files');

  const resetAttachDrag = () => {
    attachDragDepthRef.current = 0;
    setAttachDragOver(false);
  };

  const onAttachDragEnter = (e) => {
    if (!hasOsFileDrag(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    attachDragDepthRef.current += 1;
    setAttachDragOver(true);
  };

  const onAttachDragOver = (e) => {
    if (!hasOsFileDrag(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };

  const onAttachDragLeave = (e) => {
    if (!hasOsFileDrag(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    attachDragDepthRef.current = Math.max(0, attachDragDepthRef.current - 1);
    if (attachDragDepthRef.current === 0) setAttachDragOver(false);
  };

  const onAttachDrop = (e) => {
    if (!hasOsFileDrag(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    resetAttachDrag();
    if (!status.online) return;
    addFiles(e.dataTransfer?.files);
  };

  const canAcceptAttachDrop =
    attachDragOver && status.online && attachments.length < MAX_ATTACH_FILES;

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const togglePinPath = (rawPath) => {
    const path = String(rawPath || '')
      .replace(/\\/g, '/')
      .replace(/^\/+/, '')
      .trim();
    if (!path) return;
    setPinnedPaths((prev) => {
      if (prev.includes(path)) return prev.filter((p) => p !== path);
      return [...prev, path].slice(0, 8);
    });
    // Nach Anpinnen auf Mobile zum Chat wechseln
    if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches) {
      setAgentMobileTab('chat');
    }
  };

  const clearPinnedPaths = () => setPinnedPaths([]);

  const slugCodeRefAlias = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ä/gi, 'ae')
      .replace(/ö/gi, 'oe')
      .replace(/ü/gi, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-zA-Z0-9_-]+/g, '')
      .slice(0, 20);

  const makeCodeRefAlias = (kind, label, selector, path, existing, visibleText) => {
    const used = new Set(
      (existing || [])
        .map((r) => String(r.alias || '').replace(/^@/, '').toLowerCase())
        .filter(Boolean)
    );
    let base = '';
    if (kind === 'dom') {
      const quoted =
        String(label || '').match(/[„"']([^„"']{1,40})[“"']/)?.[1] ||
        String(visibleText || '').trim().slice(0, 40);
      if (quoted && quoted.length >= 1 && quoted.length <= 32) {
        base = slugCodeRefAlias(quoted);
      }
      if (!base) {
        const raw = String(label || selector || 'UI');
        const parts = raw
          .replace(/^#/, '')
          .split(/[.\s>#:[\]('"/\\„“]+/)
          .map((p) => p.trim())
          .filter(Boolean);
        const tag = parts.find((p) =>
          /^(div|span|section|header|footer|main|nav|button|a|p|h[1-6]|ul|li|img|form|input)$/i.test(p)
        );
        const skipUtil =
          /^(div|span|section|header|footer|main|nav|button|a|p|h[1-6]|ul|li|img|form|input|flex|grid|block|inline|relative|absolute|fixed|w-full|h-full|min-h-screen|text-xl|text-2xl|font-semibold|font-bold|bg-white|dark|transition|transition-all|duration-\d+|ease-\w+|scale-\S+|rounded(-\S+)?|shadow(-\S+)?)$/i;
        const meaningful = parts.find(
          (p) =>
            p.length >= 2 &&
            p.length <= 24 &&
            !skipUtil.test(p) &&
            !/^(px|py|pt|pb|pl|pr|mt|mb|ml|mr|gap|rounded|shadow|w-|h-|bg-|text-|font-|hover:|active:|dark:|sm:|md:|lg:)/i.test(
              p
            )
        );
        base = slugCodeRefAlias(meaningful || tag || parts[0] || 'UI');
      }
    } else {
      base = slugCodeRefAlias(
        String(path || 'Code')
          .split('/')
          .pop()
          ?.replace(/\.[^.]+$/, '') || 'Code'
      );
    }
    if (!base) base = kind === 'dom' ? 'UI' : 'Code';
    if (/^\d/.test(base)) base = `el${base}`;
    let candidate = base;
    let n = 2;
    while (used.has(candidate.toLowerCase())) {
      candidate = `${base}${n}`;
      n += 1;
    }
    return `@${candidate}`;
  };

  const domCodeRefDetail = (r) => {
    if (r?.sourcePath && r?.sourceLine) {
      const base = String(r.sourcePath).split('/').pop();
      return `${base}:${r.sourceLine}`;
    }
    const vt = String(r?.visibleText || '').replace(/\s+/g, ' ').trim();
    if (vt) return vt.length > 36 ? `${vt.slice(0, 36)}…` : vt;
    const quoted = String(r?.label || '').match(/[„"']([^„"']{1,40})[“"']/)?.[1];
    if (quoted) return quoted;
    return r?.label || r?.selector || 'Element';
  };

  const resolveDomCodeRefSource = async (refId, visibleText, projectIdForSearch) => {
    const q = String(visibleText || '').trim();
    const pid = String(projectIdForSearch || '').trim();
    if (!refId || !q || q.length < 2 || !pid) return;
    try {
      const res = await axios.post(
        `/api/ai/projects/${encodeURIComponent(pid)}/search`,
        { query: q, maxResults: 12 },
        { headers: authHeaders() }
      );
      const matches = Array.isArray(res.data?.matches) ? res.data.matches : [];
      const scored = matches
        .map((m) => {
          const p = String(m.path || '');
          const line = Number(m.line) || 0;
          const t = String(m.text || '');
          let score = 0;
          if (/\.(jsx|tsx|js|ts)$/i.test(p)) score += 5;
          if (/src\//i.test(p)) score += 2;
          if (/button|onClick|Auswählen|modal|pricing/i.test(t)) score += 3;
          return { path: p, line, text: t, score };
        })
        .filter((m) => m.path && m.line > 0)
        .sort((a, b) => b.score - a.score);
      const best = scored[0];
      if (!best) return;
      setCodeRefs((prev) =>
        prev.map((r) =>
          r.id === refId
            ? {
                ...r,
                sourcePath: best.path,
                sourceLine: best.line,
                sourceSnippet: String(best.text || '').slice(0, 160),
              }
            : r
        )
      );
    } catch (_) {
      /* ignore — Server löst beim Senden nach */
    }
  };

  const insertAliasIntoInput = (alias) => {
    const mention = String(alias || '').trim();
    if (!mention) return;
    setInput((prev) => {
      const cur = String(prev || '');
      if (cur.includes(mention)) return cur;
      const trimmed = cur.trimEnd();
      return trimmed ? `${trimmed} ${mention} ` : `${mention} `;
    });
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      try {
        const pos = el.value.length;
        el.setSelectionRange(pos, pos);
      } catch (_) {
        /* ignore */
      }
    });
  };

  const addCodeRef = (raw) => {
    if (!raw || !String(raw.text || '').trim()) return;
    const kind = String(raw.kind || 'code') === 'dom' ? 'dom' : 'code';
    const path = String(raw.path || '')
      .replace(/\\/g, '/')
      .replace(/^\/+/, '')
      .trim();
    const startLine = Math.max(1, Number(raw.startLine) || 1);
    const endLine = Math.max(startLine, Number(raw.endLine) || startLine);
    const text = String(raw.text || '').slice(0, 12000);
    const selector = String(raw.selector || '').trim().slice(0, 400);
    const label = String(raw.label || '').trim().slice(0, 120);
    const visibleText = String(raw.visibleText || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 80);
    const pagePath = String(raw.pagePath || '').trim().slice(0, 400);
    const id =
      String(raw.id || '').trim() ||
      `cr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    let createdAlias = '';
    setCodeRefs((prev) => {
      const next = [
        ...prev.filter((r) => {
          if (kind === 'dom' && r.kind === 'dom') {
            return !(r.selector === selector && r.pagePath === pagePath && r.text === text);
          }
          return !(
            r.path === path &&
            r.startLine === startLine &&
            r.endLine === endLine &&
            r.text === text
          );
        }),
      ];
      const alias =
        String(raw.alias || '').trim() ||
        makeCodeRefAlias(kind, label, selector, path, next, visibleText);
      createdAlias = alias;
      next.push({
        id,
        kind,
        path,
        startLine,
        endLine,
        text,
        selector,
        label,
        visibleText,
        pagePath,
        alias,
        sourcePath: raw.sourcePath || undefined,
        sourceLine: raw.sourceLine || undefined,
      });
      return next.slice(-6);
    });
    if (createdAlias) insertAliasIntoInput(createdAlias);
    if (kind === 'dom' && visibleText && projectId) {
      void resolveDomCodeRefSource(id, visibleText, projectId);
    }
    if (chatMode === 'agent') {
      // Desktop: Chat sichtbar halten; Mobile: Chat-Tab öffnen
      if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches) {
        setAgentMobileTab('chat');
      }
    }
  };

  const removeCodeRef = (id) => {
    setCodeRefs((prev) => prev.filter((r) => r.id !== id));
  };

  const clearCodeRefs = () => setCodeRefs([]);

  const rewriteComposerPrompt = async () => {
    const text = String(input || '').trim();
    if (!text || rewritingPrompt || sending || !status.online) return;
    setError('');
    setRewritingPrompt(true);
    inputBeforeRewriteRef.current = input;
    try {
      const res = await axios.post('/api/ai/rewrite-prompt', {
        text,
        mode: chatMode === 'agent' ? 'agent' : 'chat',
        model: activeModel || undefined,
        backend: selectedBackend || 'auto',
      });
      const next = String(res.data?.text || '').trim();
      if (!next) throw new Error('Leere Umschreibung');
      setPromptRewriteUndo(inputBeforeRewriteRef.current || text);
      setInput(next);
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        try {
          const pos = next.length;
          el.setSelectionRange(pos, pos);
        } catch (_) {
          /* ignore */
        }
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Prompt umschreiben fehlgeschlagen');
    } finally {
      setRewritingPrompt(false);
    }
  };

  const undoPromptRewrite = () => {
    const prev = String(promptRewriteUndo || '').trim();
    if (!prev) return;
    setInput(prev);
    setPromptRewriteUndo('');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  useEffect(() => {
    const onAdd = (ev) => addCodeRef(ev?.detail);
    const onOpenChat = () => {
      if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches) {
        setAgentMobileTab('chat');
      }
    };
    const onChangeRejected = (ev) => {
      const d = ev?.detail || {};
      if (d.revisionId) markAgentRevisionRejected(d.revisionId);
      if (d.deleted) {
        const p = normalizeAgentPath(d.path);
        const cur = normalizeAgentPath(activeFilePath);
        if (p && cur && p === cur) setActiveFilePath('');
      }
      setWorkspaceTick((n) => n + 1);
    };
    window.addEventListener('odium:agent-add-code-ref', onAdd);
    window.addEventListener('odium:agent-open-chat', onOpenChat);
    window.addEventListener('odium:agent-change-rejected', onChangeRejected);
    return () => {
      window.removeEventListener('odium:agent-add-code-ref', onAdd);
      window.removeEventListener('odium:agent-open-chat', onOpenChat);
      window.removeEventListener('odium:agent-change-rejected', onChangeRejected);
    };
  }, [activeFilePath, markAgentRevisionRejected]);

  const submitAgentAnswer = async (value, isOption = false) => {
    const jobId = String(activeJobIdRef.current || '').trim();
    const text = String(value || '').trim();
    if (!jobId || !text || agentAnswerBusy) return;
    setAgentAnswerBusy(true);
    setError('');
    try {
      await axios.post(`/api/ai/chat/jobs/${encodeURIComponent(jobId)}/answer`, {
        answer: text,
        ...(isOption ? { option: text } : {}),
      });
      setAgentAnswerDraft('');
      setMessages((prev) =>
        prev.map((m) =>
          m.agentQuestion && !m.agentQuestion.answered
            ? {
                ...m,
                agentQuestion: {
                  ...m.agentQuestion,
                  answered: true,
                  answer: text,
                },
              }
            : m
        )
      );
      setPhase('agent');
      setPhaseMessage('Agent arbeitet weiter…');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Antwort fehlgeschlagen');
    } finally {
      setAgentAnswerBusy(false);
    }
  };

  const send = async () => {
    const text = input.trim();
    const hasPins = chatMode === 'agent' && pinnedPaths.length > 0;
    const hasCodeRefs = chatMode === 'agent' && codeRefs.length > 0;
    if ((!text && !attachments.length && !hasPins && !hasCodeRefs) || sending) return;
    setError('');
    setInput('');
    setPromptRewriteUndo('');
    const pendingAttachments = attachments;
    const pendingPins = chatMode === 'agent' ? [...pinnedPaths] : [];
    const pendingCodeRefs = chatMode === 'agent' ? [...codeRefs] : [];
    setAttachments([]);
    setCodeRefs([]);
    stickToBottomRef.current = true;
    scrollToBottom();
    const streamId = `stream-${Date.now()}`;
    const userContent =
      text ||
      (pendingCodeRefs.length
        ? `Bezogen auf ${pendingCodeRefs
            .map((r) => {
              if (r.alias) return r.alias;
              if (r.kind === 'dom')
                return r.alias || r.visibleText || r.label || r.selector || 'Live-Element';
              return `${r.path}:${r.startLine}-${r.endLine}`;
            })
            .join(', ')}`
        : pendingPins.length
          ? `Bezogen auf: ${pendingPins.join(', ')}`
          : pendingAttachments.length
            ? `Anhang: ${pendingAttachments.map((a) => a.name).join(', ')}`
            : '');
    const sentAt = Date.now();
    thinkStartedAtRef.current = sentAt;
    const requestModel = String(activeModel || '').trim();
    const requestModelLabel = friendlyModelLabel(requestModel);
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: userContent,
        createdAt: sentAt,
        model: requestModel,
        modelLabel: requestModelLabel,
        attachments: pendingAttachments.map((a) => ({ name: a.name, mimeType: a.mimeType, size: a.size })),
        pinnedPaths: pendingPins,
        codeRefs: pendingCodeRefs,
      },
      {
        role: 'assistant',
        content: '',
        streaming: true,
        streamId,
        search: null,
        images: [],
        videos: [],
        audios: [],
        pdfs: [],
        downloads: [],
        createdAt: sentAt,
        model: requestModel,
        modelLabel: requestModelLabel,
        chatMode,
        agentSteps: [],
      },
    ]);
    setSending(true);
    followAgentFileRef.current = true;
    const startSteps = ['prepare'];
    if (pendingAttachments.length) startSteps.push('files');
    if (chatMode === 'agent') startSteps.push('agent');
    else if (webSearch && status.webSearchEnabled) startSteps.push('search');
    startSteps.push('think', 'write');
    setPipeline(startSteps);
    setPhase(
      pendingAttachments.length
        ? 'files'
        : chatMode === 'agent'
          ? 'agent'
          : webSearch && status.webSearchEnabled
            ? 'search'
            : 'prepare'
    );
    setPhaseMessage(
      pendingAttachments.length
        ? 'Liest Anhänge…'
        : chatMode === 'agent'
          ? 'Agent startet…'
          : webSearch && status.webSearchEnabled
            ? 'Sucht im Web…'
            : 'Bereitet Anfrage vor…'
    );

    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    abortRef.current = ctrl;

    const patchStream = (updater) => {
      setMessages((prev) =>
        prev.map((m) => (m.streamId === streamId ? updater(m) : m))
      );
    };

    try {
      let activeProjectId = projectId;
      if (chatMode === 'agent') {
        try {
          const listRes = await axios.get('/api/ai/projects');
          const projects = Array.isArray(listRes.data?.projects) ? listRes.data.projects : [];
          const stillValid = projects.some((p) => String(p.id) === String(activeProjectId));
          if (!stillValid) {
            if (projects[0]?.id) {
              activeProjectId = String(projects[0].id);
            } else {
              const created = await axios.post('/api/ai/projects', {
                name: 'Agent Workspace',
                rootType: 'server',
              });
              activeProjectId = String(created.data?.project?.id || '');
            }
            if (activeProjectId) {
              setProjectId(activeProjectId);
              try {
                localStorage.setItem(PROJECT_STORAGE_KEY, activeProjectId);
              } catch (_) {
                /* ignore */
              }
            }
          }
        } catch (projErr) {
          console.warn('Projekt vor Agent-Start:', projErr?.message || projErr);
        }
      }

      const historyPayload = messages
        .filter((m) => !m.isError && !m.streaming && (m.role === 'user' || m.role === 'assistant') && m.content)
        .slice(-24)
        .map((m) => ({ role: m.role, content: String(m.content).slice(0, 6000) }));

      const token = localStorage.getItem('authToken') || '';
      const res = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          stream: true,
          message: userContent || text,
          model: activeModel,
          backend: selectedBackend || 'auto',
          mode: chatMode === 'agent' ? 'agent' : 'chat',
          projectId: chatMode === 'agent' ? activeProjectId || undefined : undefined,
          activeFilePath: chatMode === 'agent' ? activeFilePath || undefined : undefined,
          pinnedPaths: chatMode === 'agent' && pendingPins.length ? pendingPins : undefined,
          codeRefs:
            chatMode === 'agent' && pendingCodeRefs.length
              ? pendingCodeRefs.map((r) => ({
                  kind: r.kind === 'dom' ? 'dom' : 'code',
                  path: r.path,
                  startLine: r.startLine,
                  endLine: r.endLine,
                  text: r.text,
                  selector: r.selector || undefined,
                  label: r.label || undefined,
                  visibleText: r.visibleText || undefined,
                  pagePath: r.pagePath || undefined,
                  alias: r.alias || undefined,
                  sourcePath: r.sourcePath || undefined,
                  sourceLine: r.sourceLine || undefined,
                }))
              : undefined,
          history: historyPayload,
          webSearch:
            chatMode === 'agent'
              ? 'off'
              : status.webSearchEnabled && webSearch
                ? 'always'
                : 'off',
          attachments: pendingAttachments.map((a) => ({
            name: a.name,
            mimeType: a.mimeType,
            data: a.data,
          })),
        }),
        signal: ctrl?.signal,
      });

      if (!res.ok) {
        let msg = 'Anfrage fehlgeschlagen';
        try {
          const errBody = await res.json();
          if (errBody?.error) msg = errBody.error;
        } catch (_) {
          /* ignore */
        }
        throw new Error(msg);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error('Streaming nicht unterstützt');

      const decoder = new TextDecoder();
      let buffer = '';
      let streamError = null;
      let gotDone = false;

      const handleEvent = (event, data) => {
        if (event === 'backend' && data) {
          setChatBackend({
            id: data.id,
            label: data.label,
            model: data.model,
            fallback: !!data.fallback,
            notice: data.notice || null,
          });
          const usedModel = String(data.model || '').trim();
          if (usedModel) {
            patchStream((m) => ({
              ...m,
              model: usedModel,
              modelLabel: friendlyModelLabel(usedModel),
              backendLabel: data.label || m.backendLabel,
              fallback: !!data.fallback,
            }));
          }
          if (data.fallback && data.notice) {
            setPhaseMessage(String(data.notice));
          }
        } else if (event === 'progress' && data?.phase) {
          const next = String(data.phase);
          ensurePipelineStep(next);
          setPhase(next);
          if (data.message) setPhaseMessage(String(data.message));
          else setPhaseMessage(phaseLabel(next));
        } else if (event === 'agent_step' && data?.step) {
          ensurePipelineStep('agent');
          const step = data.step;
          if (step.status === 'thinking' || step.status === 'running') {
            setPhase(
              step.tool === 'generate_image'
                ? 'image'
                : step.tool === 'generate_video'
                  ? 'video'
                : step.tool === 'generate_sound'
                  ? 'sound'
                  : step.tool === 'generate_pdf'
                    ? 'pdf'
                  : step.tool === 'ask_user'
                    ? 'agent'
                    : step.tool
                      ? 'search'
                      : 'agent'
            );
            setPhaseMessage(
              step.tool === 'ask_user'
                ? 'Wartet auf deine Antwort…'
                : step.status === 'done' &&
                    /write_file|datei_schreiben|edit_file|datei_aendern|datei_ändern|search_replace/i.test(
                      String(step.tool || '')
                    )
                  ? String(step.label || 'Datei geschrieben')
                  : String(step.label || phaseLabel('agent'))
            );
          }
          patchStream((m) => {
            const next = mergeAgentStepEntry(m.agentSteps, step);
            return { ...m, agentSteps: next, chatMode: 'agent' };
          });
        } else if (event === 'ask_user' && data?.question) {
          ensurePipelineStep('agent');
          setPhase('agent');
          setPhaseMessage('Wartet auf deine Antwort…');
          setAgentAnswerDraft('');
          patchStream((m) => ({
            ...m,
            chatMode: 'agent',
            streaming: true,
            agentQuestion: {
              id: data.id || `q-${Date.now()}`,
              question: String(data.question || ''),
              options: Array.isArray(data.options) ? data.options.map(String) : [],
              allowFreeText: data.allowFreeText !== false,
              answered: false,
              answer: '',
            },
          }));
        } else if (event === 'ask_user_answered' && data) {
          patchStream((m) => {
            if (!m.agentQuestion) return m;
            return {
              ...m,
              agentQuestion: {
                ...m.agentQuestion,
                answered: true,
                answer: String(data.answer || data.option || m.agentQuestion.answer || ''),
              },
            };
          });
          setPhase('agent');
          setPhaseMessage('Agent arbeitet weiter…');
        } else if (event === 'file_write_stream' && data) {
          const path = String(data.path || '').replace(/\\/g, '/').trim();
          if (path) {
            try {
              window.__odiumPendingLiveWrite = {
                projectId: data.projectId || projectId,
                path,
                content:
                  data.content != null
                    ? String(data.content)
                    : window.__odiumPendingLiveWrite &&
                        String(window.__odiumPendingLiveWrite.path || '').replace(/\\/g, '/') === path
                      ? window.__odiumPendingLiveWrite.content
                      : '',
                stage: data.stage || (data.done ? 'done' : 'write'),
                done: !!data.done,
              };
            } catch (_) {
              /* ignore */
            }
            revealAgentWritingFile(path, { done: !!data.done });
            // Sofort Editor zeigen, sobald Schreiben beginnt
            if (!data.done) {
              try {
                window.dispatchEvent(
                  new CustomEvent('odium:agent-workspace', { detail: { tab: 'editor' } })
                );
              } catch (_) {
                /* ignore */
              }
            }
          }
          setPhase('agent');
          setPhaseMessage(
            data.done
              ? `Datei geschrieben: ${path || '…'}`
              : `Schreibt ${path || 'Datei'}…`
          );
          // Live-Inhalt auch in Research-Timeline spiegeln
          patchStream((m) => {
            const prev = Array.isArray(m.agentSteps) ? [...m.agentSteps] : [];
            let idx = -1;
            for (let i = prev.length - 1; i >= 0; i -= 1) {
              const t = String(prev[i].tool || '').toLowerCase();
              if (
                t === 'write_file' ||
                t === 'datei_schreiben' ||
                t === 'edit_file' ||
                t === 'datei_aendern' ||
                t === 'datei_ändern' ||
                t === 'search_replace'
              ) {
                idx = i;
                break;
              }
            }
            if (idx < 0) {
              prev.push({
                index: prev.length,
                tool: 'write_file',
                status: data.done ? 'done' : 'running',
                label: path ? `Datei schreiben: ${path.slice(0, 48)}` : 'Datei schreiben',
                args: path ? { path } : null,
                details: {
                  path,
                  content: data.content != null ? String(data.content) : '',
                  ...(data.diff ? { diff: data.diff, created: !!data.created } : {}),
                  ...(data.revisionId ? { revisionId: String(data.revisionId) } : {}),
                  ...(data.rejected ? { rejected: true } : {}),
                },
                preview: '',
              });
            } else {
              const cur = prev[idx];
              prev[idx] = {
                ...cur,
                status: data.done ? 'done' : cur.status === 'done' ? 'done' : 'running',
                label: cur.label || (path ? `Datei schreiben: ${path.slice(0, 48)}` : 'Datei schreiben'),
                args: { ...(cur.args || {}), ...(path ? { path } : {}) },
                details: {
                  ...(cur.details || {}),
                  path: path || cur.details?.path,
                  content:
                    data.content != null ? String(data.content) : cur.details?.content || '',
                  ...(data.diff
                    ? { diff: data.diff, created: !!data.created }
                    : {}),
                  ...(data.revisionId ? { revisionId: String(data.revisionId) } : {}),
                  ...(data.rejected ? { rejected: true } : {}),
                },
              };
            }
            return { ...m, agentSteps: prev, chatMode: 'agent' };
          });
          const fireLive = () => {
            try {
              window.dispatchEvent(
                new CustomEvent('odium:agent-file-live', {
                  detail: {
                    ...data,
                    projectId: data.projectId || projectId,
                    path,
                  },
                })
              );
            } catch (_) {
              /* ignore */
            }
            if (data.done) {
              try {
                const pendingPath = String(window.__odiumPendingLiveWrite?.path || '').replace(
                  /\\/g,
                  '/'
                );
                if (!path || pendingPath === path || pendingPath === String(path).replace(/\\/g, '/')) {
                  window.__odiumPendingLiveWrite = null;
                }
              } catch (_) {
                /* ignore */
              }
              setWorkspaceTick((n) => n + 1);
              if (data.diff && !data.diff?.unchanged) {
                try {
                  window.dispatchEvent(
                    new CustomEvent('odium:agent-workspace', { detail: { tab: 'editor' } })
                  );
                } catch (_) {
                  /* ignore */
                }
              }
            }
          };
          // Write-Chunks sofort — kein rAF-Delay (sonst stapeln sich Updates und wirken wie Dump)
          if (data.stage === 'write' || data.stage === 'start') {
            fireLive();
          } else if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => setTimeout(fireLive, 0));
          } else {
            setTimeout(fireLive, 0);
          }
        } else if (event === 'project_changed' && data) {
          setWorkspaceTick((n) => n + 1);
          if (data.path && data.action === 'write') {
            revealAgentWritingFile(String(data.path), { done: true });
            // Kein file-live ohne content — das hat den Editor geleert und „schreibt…“ zum Flackern gebracht.
            // Diff kommt bereits über file_write_stream (done).
          }
          const projectListAction = String(data.action || '');
          if (
            /^(create_project|rename_project|delete_project|switch_project)$/i.test(projectListAction)
          ) {
            try {
              window.dispatchEvent(
                new CustomEvent('odium:agent-projects', { detail: data })
              );
            } catch (_) {
              /* ignore */
            }
          }
          if (data.action === 'preview' || data.action === 'check' || data.action === 'install') {
            if (data.action === 'preview') {
              setPreviewTick((n) => n + 1);
            }
            try {
              window.dispatchEvent(
                new CustomEvent('odium:agent-workspace', {
                  detail: {
                    // Kein Auto-Switch auf Live — Nutzer bleibt im Editor/Code
                    terminal: true,
                    // Preview-Reload nur über previewTick / Ticket-Event — kein Doppel-Start
                    refreshPreview: false,
                    refreshTerminal: true,
                    ticket: data.action === 'preview' ? data.ticket || undefined : undefined,
                    previewPath: data.action === 'preview' ? data.previewPath || undefined : undefined,
                    mode: data.action === 'preview' ? data.mode || undefined : undefined,
                  },
                })
              );
            } catch (_) {
              /* ignore */
            }
          }
        } else if (event === 'project_bound' && data?.projectId) {
          const id = String(data.projectId);
          setProjectId(id);
          setActiveFilePath('');
          try {
            localStorage.setItem(PROJECT_STORAGE_KEY, id);
          } catch (_) {
            /* ignore */
          }
          setWorkspaceTick((n) => n + 1);
          try {
            window.dispatchEvent(
              new CustomEvent('odium:agent-projects', { detail: data })
            );
          } catch (_) {
            /* ignore */
          }
          if (data.created) {
            setPhaseMessage(`Projekt „${data.name || id}“ angelegt`);
          } else if (data.switched) {
            setPhaseMessage(`Projekt „${data.name || id}“ aktiv`);
          }
        } else if (event === 'reset') {
          ensurePipelineStep('search');
          setPhase('search');
          setPhaseMessage('Sucht nachträglich im Web…');
          patchStream((m) => ({
            ...m,
            content: '',
            streaming: true,
            search: null,
          }));
        } else if (event === 'image' && data?.image) {
          ensurePipelineStep('image');
          setPhase('image');
          setPhaseMessage(data.image.error ? 'Bild fehlgeschlagen' : 'Bild erzeugt');
          if (!data.image.error && data.image.url) {
            notifyMediaLibraryChanged({ mediaId: data.image.mediaId, url: data.image.url });
            patchStream((m) => ({
              ...m,
              images: [...(m.images || []).filter((img) => img.url !== data.image.url), data.image],
            }));
          } else if (data.image.error) {
            patchStream((m) => ({
              ...m,
              imageError: data.image.error,
            }));
          }
        } else if (event === 'video' && data?.video) {
          ensurePipelineStep('video');
          setPhase('video');
          setPhaseMessage(data.video.error ? 'Video fehlgeschlagen' : 'Video erzeugt');
          if (!data.video.error && data.video.url) {
            notifyMediaLibraryChanged({ mediaId: data.video.mediaId, url: data.video.url });
            patchStream((m) => ({
              ...m,
              videos: [...(m.videos || []).filter((v) => v.url !== data.video.url), data.video],
            }));
          } else if (data.video.error) {
            patchStream((m) => ({
              ...m,
              videoError: data.video.error,
            }));
          }
        } else if (event === 'audio' && data?.audio) {
          ensurePipelineStep('sound');
          setPhase('sound');
          setPhaseMessage(data.audio.error ? 'Sound fehlgeschlagen' : 'Sound erzeugt');
          if (!data.audio.error && data.audio.url) {
            notifyMediaLibraryChanged({ mediaId: data.audio.mediaId, url: data.audio.url });
            patchStream((m) => ({
              ...m,
              audios: [...(m.audios || []).filter((a) => a.url !== data.audio.url), data.audio],
            }));
          } else if (data.audio.error) {
            patchStream((m) => ({
              ...m,
              audioError: data.audio.error,
            }));
          }
        } else if (event === 'pdf' && data?.pdf) {
          ensurePipelineStep('pdf');
          setPhase('pdf');
          setPhaseMessage(data.pdf.error ? 'PDF fehlgeschlagen' : 'PDF erzeugt');
          if (!data.pdf.error && data.pdf.url) {
            notifyMediaLibraryChanged({ mediaId: data.pdf.mediaId, url: data.pdf.url });
            patchStream((m) => ({
              ...m,
              pdfs: [...(m.pdfs || []).filter((p) => p.url !== data.pdf.url), data.pdf],
            }));
          } else if (data.pdf.error) {
            patchStream((m) => ({
              ...m,
              pdfError: data.pdf.error,
            }));
          }
        } else if (event === 'search' && data?.search) {
          const search = data.search;
          ensurePipelineStep('search');
          patchStream((m) => ({
            ...m,
            search: search.searched
              ? {
                  query: search.query,
                  queries: search.queries || m.search?.queries || undefined,
                  engine: search.engine,
                  error: search.error,
                  sources: search.sources || [],
                }
              : null,
          }));
        } else if (event === 'job' && data?.id) {
          activeJobIdRef.current = String(data.id);
          storeJobId(data.id);
          patchStream((m) => ({ ...m, jobId: String(data.id), pendingJob: true }));
        } else if (event === 'snapshot' && data) {
          ensurePipelineStep('write');
          setPhase('write');
          setPhaseMessage('Setzt Antwort fort…');
          patchStream((m) => ({
            ...m,
            content: String(data.reply || m.content || ''),
            streaming: data.status === 'running' || data.status == null,
            pendingJob: data.status === 'running',
          }));
        } else if (event === 'token' && data) {
          ensurePipelineStep('write');
          setPhase('write');
          setPhaseMessage('Schreibt Antwort…');
          if (data.replace && data.reply != null) {
            patchStream((m) => ({
              ...m,
              content: String(data.reply || ''),
              streaming: true,
            }));
          } else if (data.delta) {
            patchStream((m) => ({
              ...m,
              content: `${m.content || ''}${data.delta}`,
              streaming: true,
            }));
          }
        } else if (event === 'think' && data?.delta) {
          ensurePipelineStep('think');
          setPhase('think');
          setPhaseMessage('Denkt nach…');
          patchStream((m) => applyThinkDeltaToMessage(m, data.delta));
        } else if (event === 'downloads' && Array.isArray(data?.files) && data.files.length) {
          const safeFiles = filterRealDownloads(data.files);
          if (safeFiles.length) {
            patchStream((m) => ({
              ...m,
              downloads: [...(m.downloads || []), ...safeFiles],
            }));
          }
        } else if (event === 'done') {
          gotDone = true;
          clearStoredJobId();
          activeJobIdRef.current = '';
          const reply = String(data?.reply || '').trim() ||
            (data?.video?.error || data?.image?.error || data?.audio?.error
              ? ''
              : '(leere Antwort)');
          const search = data?.search || null;
          const durationMs = Date.now() - thinkStartedAtRef.current;
          if (data?.usage) {
            const promptTokens = Number(data.usage.promptTokens) || 0;
            const completionTokens = Number(data.usage.completionTokens) || 0;
            const totalTokens =
              Number(data.usage.totalTokens) || promptTokens + completionTokens;
            const win = Number(data.usage.contextWindow) || contextWindow;
            setLastUsage({
              promptTokens,
              completionTokens,
              totalTokens,
              contextWindow: win,
            });
            if (win > 0) {
              setStatus((prev) => ({
                ...prev,
                contextWindow: win,
                reservedOutputTokens:
                  Number(data.usage.reservedOutputTokens) || prev.reservedOutputTokens,
              }));
            }
          }
          const doneDownloads = Array.isArray(data?.downloads)
            ? filterRealDownloads(data.downloads)
            : null;
          if (data?.image?.url && !data?.image?.error) {
            notifyMediaLibraryChanged({ mediaId: data.image.mediaId, url: data.image.url });
          }
          if (data?.video?.url && !data?.video?.error) {
            notifyMediaLibraryChanged({ mediaId: data.video.mediaId, url: data.video.url });
          }
          if (data?.audio?.url && !data?.audio?.error) {
            notifyMediaLibraryChanged({ mediaId: data.audio.mediaId, url: data.audio.url });
          }
          if (data?.pdf?.url && !data?.pdf?.error) {
            notifyMediaLibraryChanged({ mediaId: data.pdf.mediaId, url: data.pdf.url });
          }
          patchStream((m) => ({
            ...m,
            role: 'assistant',
            content: reply || m.content || (m.videoError || m.imageError || m.audioError ? '' : '(leere Antwort)'),
            streaming: false,
            streamId: undefined,
            durationMs,
            createdAt: m.createdAt || Date.now(),
            model: String(data?.model || m.model || '').trim() || m.model,
            modelLabel:
              friendlyModelLabel(data?.model || m.model) || m.modelLabel,
            images: data?.image?.url
              ? [...(m.images || []).filter((img) => img.url !== data.image.url), data.image]
              : m.images || [],
            imageError: data?.image?.error || m.imageError || null,
            videos: data?.video?.url
              ? [...(m.videos || []).filter((v) => v.url !== data.video.url), data.video]
              : m.videos || [],
            videoError: data?.video?.error || m.videoError || null,
            audios: data?.audio?.url
              ? [...(m.audios || []).filter((a) => a.url !== data.audio.url), data.audio]
              : m.audios || [],
            audioError: data?.audio?.error || m.audioError || null,
            pdfs: data?.pdf?.url
              ? [...(m.pdfs || []).filter((p) => p.url !== data.pdf.url), data.pdf]
              : m.pdfs || [],
            pdfError: data?.pdf?.error || m.pdfError || null,
            downloads: doneDownloads?.length ? doneDownloads : filterRealDownloads(m.downloads || []),
            pendingJob: false,
            jobId: undefined,
            chatMode: data?.mode === 'agent' ? 'agent' : m.chatMode || chatMode,
            agentSteps: Array.isArray(data?.agentSteps) ? data.agentSteps : m.agentSteps || [],
            search: search?.searched
              ? {
                  query: search.query,
                  queries: search.queries || undefined,
                  engine: search.engine,
                  error: search.error,
                  sources: search.sources || [],
                }
              : m.search || null,
          }));
          try {
            window.__odiumPendingLiveWrite = null;
          } catch (_) {
            /* ignore */
          }
        } else if (event === 'error') {
          streamError = friendlyStreamError(data?.error || 'Anfrage fehlgeschlagen');
          clearStoredJobId();
          activeJobIdRef.current = '';
          try {
            window.__odiumPendingLiveWrite = null;
          } catch (_) {
            /* ignore */
          }
        } else if (event === 'cancelled') {
          streamError = 'Abgebrochen';
          clearStoredJobId();
          activeJobIdRef.current = '';
          try {
            window.__odiumPendingLiveWrite = null;
          } catch (_) {
            /* ignore */
          }
          const partial = String(data?.reply || '').trim();
          const durationMs = Date.now() - thinkStartedAtRef.current;
          if (partial) {
            patchStream((m) => ({
              ...m,
              content: partial,
              streaming: false,
              streamId: undefined,
              pendingJob: false,
              durationMs,
            }));
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';
        for (const chunk of chunks) {
          const lines = chunk.split('\n');
          let event = 'message';
          const dataLines = [];
          for (const line of lines) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
          }
          if (!dataLines.length) continue;
          let data = null;
          try {
            data = JSON.parse(dataLines.join('\n'));
          } catch (_) {
            continue;
          }
          handleEvent(event, data);
        }
      }

      if (streamError === 'Abgebrochen') {
        setMessages((prev) => {
          const hasPartial = prev.some((m) => m.streamId === streamId && String(m.content || '').trim());
          if (hasPartial) {
            return prev.map((m) =>
              m.streamId === streamId
                ? { ...m, streaming: false, streamId: undefined }
                : m
            );
          }
          return prev.map((m) =>
            m.streamId === streamId
              ? { ...m, role: 'assistant', content: 'Antwort abgebrochen.', isError: true, streaming: false, streamId: undefined }
              : m
          );
        });
      } else if (streamError) {
        setError(streamError);
        const durationMs = Date.now() - thinkStartedAtRef.current;
        patchStream((m) => ({
          ...m,
          role: 'assistant',
          content: `Fehler: ${streamError}`,
          isError: true,
          streaming: false,
          streamId: undefined,
          durationMs,
        }));
      } else if (!gotDone) {
        // Stream endete ohne done — vorhandenen Text finalisieren
        const durationMs = Date.now() - thinkStartedAtRef.current;
        setMessages((prev) =>
          prev.map((m) =>
            m.streamId === streamId
              ? {
                  ...m,
                  content:
                    String(m.content || '').trim() ||
                    (m.videoError || m.imageError || m.audioError ? '' : '(leere Antwort)'),
                  streaming: false,
                  streamId: undefined,
                  durationMs: m.durationMs != null ? m.durationMs : durationMs,
                }
              : m
          )
        );
      }
    } catch (err) {
      const aborted =
        ctrl?.signal?.aborted ||
        err?.name === 'AbortError' ||
        err?.name === 'CanceledError';
      const durationMs = Date.now() - thinkStartedAtRef.current;
      if (aborted) {
        const jobId = activeJobIdRef.current || readStoredJobId();
        // Disconnect/Reload: Job läuft serverseitig weiter
        if (jobId) {
          setError('');
          setMessages((prev) =>
            prev.map((m) =>
              m.streamId === streamId
                ? {
                    ...m,
                    streaming: false,
                    pendingJob: true,
                    jobId,
                    streamId: undefined,
                    durationMs: m.durationMs != null ? m.durationMs : durationMs,
                  }
                : m
            )
          );
        } else {
          setError('');
          setMessages((prev) => {
            const idx = prev.findIndex((m) => m.streamId === streamId);
            if (idx < 0) {
              return [
                ...prev,
                {
                  role: 'assistant',
                  content: 'Antwort abgebrochen.',
                  isError: true,
                  durationMs,
                },
              ];
            }
            const cur = prev[idx];
            if (String(cur.content || '').trim()) {
              return prev.map((m, i) =>
                i === idx
                  ? {
                      ...m,
                      streaming: false,
                      streamId: undefined,
                      pendingJob: false,
                      durationMs: m.durationMs != null ? m.durationMs : durationMs,
                    }
                  : m
              );
            }
            return prev.map((m, i) =>
              i === idx
                ? {
                    ...m,
                    role: 'assistant',
                    content: 'Antwort abgebrochen.',
                    isError: true,
                    streaming: false,
                    streamId: undefined,
                    pendingJob: false,
                    durationMs,
                  }
                : m
            );
          });
        }
      } else {
        const msg = err.message || 'Anfrage fehlgeschlagen';
        setError(msg);
        clearStoredJobId();
        activeJobIdRef.current = '';
        setMessages((prev) =>
          prev.map((m) =>
            m.streamId === streamId
              ? {
                  ...m,
                  role: 'assistant',
                  content: `Fehler: ${msg}`,
                  isError: true,
                  streaming: false,
                  streamId: undefined,
                  pendingJob: false,
                  durationMs,
                }
              : m
          )
        );
      }
    } finally {
      abortRef.current = null;
      setSending(false);
      setPhase('');
      setPhaseMessage('');
      setElapsedMs(0);
      // Auf Touch kein Refocus — sonst springt die Tastatur hoch und scrollt den Chat.
      const coarse =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(pointer: coarse)').matches;
      if (!coarse) inputRef.current?.focus();
    }
  };

  const resumeJob = async (jobId) => {
    const id = String(jobId || '').trim();
    if (!id || sending) return;
    const streamId = `resume-${id}`;
    thinkStartedAtRef.current = Date.now();
    activeJobIdRef.current = id;
    storeJobId(id);
    setError('');
    setSending(true);
    followAgentFileRef.current = true;
    setPipeline(['prepare', 'think', 'write']);
    setPhase('write');
    setPhaseMessage('Setzt Antwort fort…');

    setMessages((prev) => {
      const hasPending = prev.some((m) => m.pendingJob || m.jobId === id || m.streaming);
      if (hasPending) {
        return prev.map((m) =>
          m.pendingJob || m.jobId === id || m.streaming
            ? {
                ...m,
                role: 'assistant',
                streaming: true,
                pendingJob: true,
                streamId,
                jobId: id,
                isError: false,
              }
            : m
        );
      }
      return [
        ...prev,
        {
          role: 'assistant',
          content: '',
          streaming: true,
          pendingJob: true,
          streamId,
          jobId: id,
          createdAt: Date.now(),
        },
      ];
    });

    const patchStream = (updater) => {
      setMessages((prev) =>
        prev.map((m) => (m.streamId === streamId || m.jobId === id ? updater(m) : m))
      );
    };

    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    abortRef.current = ctrl;

    try {
      const token = localStorage.getItem('authToken') || '';
      const res = await fetch(`/api/ai/chat/jobs/${encodeURIComponent(id)}/stream`, {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: ctrl?.signal,
      });
      if (!res.ok) {
        if (res.status === 404) {
          clearStoredJobId();
          activeJobIdRef.current = '';
          throw new Error('Antwort-Job nicht mehr verfügbar');
        }
        throw new Error('Fortsetzen fehlgeschlagen');
      }
      const reader = res.body?.getReader();
      if (!reader) throw new Error('Streaming nicht unterstützt');
      const decoder = new TextDecoder();
      let buffer = '';
      let streamError = null;
      let gotDone = false;

      const handleEvent = (event, data) => {
        if (event === 'job' && data?.id) {
          activeJobIdRef.current = String(data.id);
          storeJobId(data.id);
        } else if (event === 'snapshot' && data) {
          const awaiting =
            data.status === 'awaiting_user' || data.status === 'running' || !data.status;
          setPhase(data.status === 'awaiting_user' ? 'agent' : 'write');
          setPhaseMessage(
            data.status === 'awaiting_user' ? 'Wartet auf deine Antwort…' : 'Setzt Antwort fort…'
          );
          patchStream((m) => ({
            ...m,
            content: String(data.reply || m.content || ''),
            streaming: awaiting,
            pendingJob: awaiting,
            ...(data.pendingQuestion?.question
              ? {
                  agentQuestion: {
                    id: data.pendingQuestion.id || `q-${Date.now()}`,
                    question: String(data.pendingQuestion.question || ''),
                    options: Array.isArray(data.pendingQuestion.options)
                      ? data.pendingQuestion.options.map(String)
                      : [],
                    allowFreeText: data.pendingQuestion.allowFreeText !== false,
                    answered: false,
                    answer: '',
                  },
                }
              : {}),
          }));
        } else if (event === 'progress' && data?.phase) {
          setPhase(String(data.phase));
          if (data.message) setPhaseMessage(String(data.message));
        } else if (event === 'agent_step' && data?.step) {
          ensurePipelineStep('agent');
          const step = data.step;
          if (step.status === 'thinking' || step.status === 'running') {
            setPhase(
              step.tool === 'generate_image'
                ? 'image'
                : step.tool === 'generate_video'
                  ? 'video'
                : step.tool === 'generate_sound'
                  ? 'sound'
                  : step.tool === 'generate_pdf'
                    ? 'pdf'
                  : step.tool === 'ask_user'
                    ? 'agent'
                    : step.tool
                      ? 'search'
                      : 'agent'
            );
            setPhaseMessage(
              step.tool === 'ask_user'
                ? 'Wartet auf deine Antwort…'
                : step.status === 'done' &&
                    /write_file|datei_schreiben|edit_file|datei_aendern|datei_ändern|search_replace/i.test(
                      String(step.tool || '')
                    )
                  ? String(step.label || 'Datei geschrieben')
                  : String(step.label || phaseLabel('agent'))
            );
          }
          patchStream((m) => {
            const next = mergeAgentStepEntry(m.agentSteps, step);
            return { ...m, agentSteps: next, chatMode: 'agent' };
          });
        } else if (event === 'file_write_stream' && data) {
          const path = String(data.path || '').replace(/\\/g, '/').trim();
          if (path) {
            try {
              window.__odiumPendingLiveWrite = {
                projectId: data.projectId || projectId,
                path,
                content:
                  data.content != null
                    ? String(data.content)
                    : window.__odiumPendingLiveWrite &&
                        String(window.__odiumPendingLiveWrite.path || '').replace(/\\/g, '/') === path
                      ? window.__odiumPendingLiveWrite.content
                      : '',
                stage: data.stage || (data.done ? 'done' : 'write'),
                done: !!data.done,
              };
            } catch (_) {
              /* ignore */
            }
            revealAgentWritingFile(path, { done: !!data.done });
            if (!data.done) {
              try {
                window.dispatchEvent(
                  new CustomEvent('odium:agent-workspace', { detail: { tab: 'editor' } })
                );
              } catch (_) {
                /* ignore */
              }
            }
          }
          setPhase('agent');
          setPhaseMessage(
            data.done ? `Datei geschrieben: ${path || '…'}` : `Schreibt ${path || 'Datei'}…`
          );
          patchStream((m) => {
            const prev = Array.isArray(m.agentSteps) ? [...m.agentSteps] : [];
            let idx = -1;
            for (let i = prev.length - 1; i >= 0; i -= 1) {
              const t = String(prev[i].tool || '').toLowerCase();
              if (
                t === 'write_file' ||
                t === 'datei_schreiben' ||
                t === 'edit_file' ||
                t === 'datei_aendern' ||
                t === 'datei_ändern' ||
                t === 'search_replace'
              ) {
                idx = i;
                break;
              }
            }
            if (idx < 0) {
              prev.push({
                index: prev.length,
                tool: 'write_file',
                status: data.done ? 'done' : 'running',
                label: path ? `Datei schreiben: ${path.slice(0, 48)}` : 'Datei schreiben',
                args: path ? { path } : null,
                details: {
                  path,
                  content: data.content != null ? String(data.content) : '',
                  ...(data.diff ? { diff: data.diff, created: !!data.created } : {}),
                  ...(data.revisionId ? { revisionId: String(data.revisionId) } : {}),
                  ...(data.rejected ? { rejected: true } : {}),
                },
                preview: '',
              });
            } else {
              const cur = prev[idx];
              prev[idx] = {
                ...cur,
                status: data.done ? 'done' : cur.status === 'done' ? 'done' : 'running',
                label:
                  cur.label ||
                  (path ? `Datei schreiben: ${path.slice(0, 48)}` : 'Datei schreiben'),
                args: { ...(cur.args || {}), ...(path ? { path } : {}) },
                details: {
                  ...(cur.details || {}),
                  path: path || cur.details?.path,
                  content:
                    data.content != null ? String(data.content) : cur.details?.content || '',
                  ...(data.diff ? { diff: data.diff, created: !!data.created } : {}),
                  ...(data.revisionId ? { revisionId: String(data.revisionId) } : {}),
                  ...(data.rejected ? { rejected: true } : {}),
                },
              };
            }
            return { ...m, agentSteps: prev, chatMode: 'agent' };
          });
          const fireLive = () => {
            try {
              window.dispatchEvent(
                new CustomEvent('odium:agent-file-live', {
                  detail: {
                    ...data,
                    projectId: data.projectId || projectId,
                    path,
                  },
                })
              );
            } catch (_) {
              /* ignore */
            }
            if (data.done) {
              try {
                const pendingPath = String(window.__odiumPendingLiveWrite?.path || '').replace(
                  /\\/g,
                  '/'
                );
                if (!path || pendingPath === path || pendingPath === String(path).replace(/\\/g, '/')) {
                  window.__odiumPendingLiveWrite = null;
                }
              } catch (_) {
                /* ignore */
              }
              setWorkspaceTick((n) => n + 1);
              if (data.diff && !data.diff?.unchanged) {
                try {
                  window.dispatchEvent(
                    new CustomEvent('odium:agent-workspace', { detail: { tab: 'editor' } })
                  );
                } catch (_) {
                  /* ignore */
                }
              }
            }
          };
          // Write-Chunks sofort — kein rAF-Delay (sonst stapeln sich Updates und wirken wie Dump)
          if (data.stage === 'write' || data.stage === 'start') {
            fireLive();
          } else if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => setTimeout(fireLive, 0));
          } else {
            setTimeout(fireLive, 0);
          }
        } else if (event === 'project_changed' && data) {
          setWorkspaceTick((n) => n + 1);
          if (data.path && data.action === 'write') {
            revealAgentWritingFile(String(data.path), { done: true });
            // Kein file-live ohne content — das hat den Editor geleert und „schreibt…“ zum Flackern gebracht.
            // Diff kommt bereits über file_write_stream (done).
          }
          const projectListAction = String(data.action || '');
          if (
            /^(create_project|rename_project|delete_project|switch_project)$/i.test(projectListAction)
          ) {
            try {
              window.dispatchEvent(
                new CustomEvent('odium:agent-projects', { detail: data })
              );
            } catch (_) {
              /* ignore */
            }
          }
          if (data.action === 'preview' || data.action === 'check' || data.action === 'install') {
            if (data.action === 'preview') {
              setPreviewTick((n) => n + 1);
            }
            try {
              window.dispatchEvent(
                new CustomEvent('odium:agent-workspace', {
                  detail: {
                    // Kein Auto-Switch auf Live — Nutzer bleibt im Editor/Code
                    terminal: true,
                    // Preview-Reload nur über previewTick / Ticket-Event — kein Doppel-Start
                    refreshPreview: false,
                    refreshTerminal: true,
                    ticket: data.action === 'preview' ? data.ticket || undefined : undefined,
                    previewPath: data.action === 'preview' ? data.previewPath || undefined : undefined,
                    mode: data.action === 'preview' ? data.mode || undefined : undefined,
                  },
                })
              );
            } catch (_) {
              /* ignore */
            }
          }
        } else if (event === 'project_bound' && data?.projectId) {
          const id = String(data.projectId);
          setProjectId(id);
          setActiveFilePath('');
          try {
            localStorage.setItem(PROJECT_STORAGE_KEY, id);
          } catch (_) {
            /* ignore */
          }
          setWorkspaceTick((n) => n + 1);
          try {
            window.dispatchEvent(
              new CustomEvent('odium:agent-projects', { detail: data })
            );
          } catch (_) {
            /* ignore */
          }
        } else if (event === 'backend' && data) {
          setChatBackend({
            id: data.id,
            label: data.label,
            model: data.model,
            fallback: !!data.fallback,
            notice: data.notice || null,
          });
        } else if (event === 'ask_user' && data?.question) {
          setPhase('agent');
          setPhaseMessage('Wartet auf deine Antwort…');
          patchStream((m) => ({
            ...m,
            chatMode: 'agent',
            streaming: true,
            pendingJob: true,
            agentQuestion: {
              id: data.id || `q-${Date.now()}`,
              question: String(data.question || ''),
              options: Array.isArray(data.options) ? data.options.map(String) : [],
              allowFreeText: data.allowFreeText !== false,
              answered: false,
              answer: '',
            },
          }));
        } else if (event === 'ask_user_answered' && data) {
          patchStream((m) => {
            if (!m.agentQuestion) return m;
            return {
              ...m,
              agentQuestion: {
                ...m.agentQuestion,
                answered: true,
                answer: String(data.answer || data.option || m.agentQuestion.answer || ''),
              },
            };
          });
          setPhase('agent');
          setPhaseMessage('Agent arbeitet weiter…');
        } else if (event === 'token' && data) {
          setPhase('write');
          setPhaseMessage('Schreibt Antwort…');
          if (data.replace && data.reply != null) {
            patchStream((m) => ({ ...m, content: String(data.reply || ''), streaming: true }));
          } else if (data.delta) {
            patchStream((m) => ({
              ...m,
              content: `${m.content || ''}${data.delta}`,
              streaming: true,
            }));
          }
        } else if (event === 'think' && data?.delta) {
          setPhase('think');
          setPhaseMessage('Denkt nach…');
          patchStream((m) => applyThinkDeltaToMessage(m, data.delta));
        } else if (event === 'downloads' && Array.isArray(data?.files) && data.files.length) {
          const safeFiles = filterRealDownloads(data.files);
          if (safeFiles.length) {
            patchStream((m) => ({
              ...m,
              downloads: [...(m.downloads || []), ...safeFiles],
            }));
          }
        } else if (event === 'image' && data?.image?.url) {
          notifyMediaLibraryChanged({ mediaId: data.image.mediaId, url: data.image.url });
          patchStream((m) => ({
            ...m,
            images: [...(m.images || []).filter((img) => img.url !== data.image.url), data.image],
          }));
        } else if (event === 'video' && data?.video) {
          ensurePipelineStep('video');
          setPhase('video');
          setPhaseMessage(data.video.error ? 'Video fehlgeschlagen' : 'Video erzeugt');
          if (!data.video.error && data.video.url) {
            notifyMediaLibraryChanged({ mediaId: data.video.mediaId, url: data.video.url });
            patchStream((m) => ({
              ...m,
              videos: [...(m.videos || []).filter((v) => v.url !== data.video.url), data.video],
            }));
          } else if (data.video.error) {
            patchStream((m) => ({
              ...m,
              videoError: data.video.error,
            }));
          }
        } else if (event === 'audio' && data?.audio) {
          ensurePipelineStep('sound');
          setPhase('sound');
          setPhaseMessage(data.audio.error ? 'Sound fehlgeschlagen' : 'Sound erzeugt');
          if (!data.audio.error && data.audio.url) {
            notifyMediaLibraryChanged({ mediaId: data.audio.mediaId, url: data.audio.url });
            patchStream((m) => ({
              ...m,
              audios: [...(m.audios || []).filter((a) => a.url !== data.audio.url), data.audio],
            }));
          } else if (data.audio.error) {
            patchStream((m) => ({
              ...m,
              audioError: data.audio.error,
            }));
          }
        } else if (event === 'pdf' && data?.pdf) {
          ensurePipelineStep('pdf');
          setPhase('pdf');
          setPhaseMessage(data.pdf.error ? 'PDF fehlgeschlagen' : 'PDF erzeugt');
          if (!data.pdf.error && data.pdf.url) {
            notifyMediaLibraryChanged({ mediaId: data.pdf.mediaId, url: data.pdf.url });
            patchStream((m) => ({
              ...m,
              pdfs: [...(m.pdfs || []).filter((p) => p.url !== data.pdf.url), data.pdf],
            }));
          } else if (data.pdf.error) {
            patchStream((m) => ({
              ...m,
              pdfError: data.pdf.error,
            }));
          }
        } else if (event === 'search' && data?.search?.searched) {
          patchStream((m) => ({
            ...m,
            search: {
              query: data.search.query,
              queries: data.search.queries || m.search?.queries || undefined,
              engine: data.search.engine,
              error: data.search.error,
              sources: data.search.sources || [],
            },
          }));
        } else if (event === 'done') {
          gotDone = true;
          clearStoredJobId();
          activeJobIdRef.current = '';
          const reply = String(data?.reply || '').trim() ||
            (data?.video?.error || data?.image?.error || data?.audio?.error
              ? ''
              : '(leere Antwort)');
          const durationMs = Date.now() - thinkStartedAtRef.current;
          const doneDownloads = Array.isArray(data?.downloads)
            ? filterRealDownloads(data.downloads)
            : null;
          if (data?.image?.url && !data?.image?.error) {
            notifyMediaLibraryChanged({ mediaId: data.image.mediaId, url: data.image.url });
          }
          if (data?.video?.url && !data?.video?.error) {
            notifyMediaLibraryChanged({ mediaId: data.video.mediaId, url: data.video.url });
          }
          if (data?.audio?.url && !data?.audio?.error) {
            notifyMediaLibraryChanged({ mediaId: data.audio.mediaId, url: data.audio.url });
          }
          if (data?.pdf?.url && !data?.pdf?.error) {
            notifyMediaLibraryChanged({ mediaId: data.pdf.mediaId, url: data.pdf.url });
          }
          patchStream((m) => ({
            ...m,
            content: reply || m.content || (m.videoError || m.imageError || m.audioError ? '' : '(leere Antwort)'),
            streaming: false,
            pendingJob: false,
            streamId: undefined,
            jobId: undefined,
            durationMs,
            downloads: doneDownloads?.length ? doneDownloads : filterRealDownloads(m.downloads || []),
            images: data?.image?.url
              ? [...(m.images || []).filter((img) => img.url !== data.image.url), data.image]
              : m.images || [],
            videos: data?.video?.url
              ? [...(m.videos || []).filter((v) => v.url !== data.video.url), data.video]
              : m.videos || [],
            videoError: data?.video?.error || m.videoError || null,
            audios: data?.audio?.url
              ? [...(m.audios || []).filter((a) => a.url !== data.audio.url), data.audio]
              : m.audios || [],
            audioError: data?.audio?.error || m.audioError || null,
            pdfs: data?.pdf?.url
              ? [...(m.pdfs || []).filter((p) => p.url !== data.pdf.url), data.pdf]
              : m.pdfs || [],
            pdfError: data?.pdf?.error || m.pdfError || null,
            model: String(data?.model || m.model || '').trim() || m.model,
            modelLabel: friendlyModelLabel(data?.model || m.model) || m.modelLabel,
            chatMode: data?.mode === 'agent' ? 'agent' : m.chatMode,
            agentSteps: Array.isArray(data?.agentSteps) ? data.agentSteps : m.agentSteps || [],
          }));
          try {
            window.__odiumPendingLiveWrite = null;
          } catch (_) {
            /* ignore */
          }
        } else if (event === 'error') {
          streamError = friendlyStreamError(data?.error || 'Anfrage fehlgeschlagen');
          clearStoredJobId();
          activeJobIdRef.current = '';
          try {
            window.__odiumPendingLiveWrite = null;
          } catch (_) {
            /* ignore */
          }
        } else if (event === 'cancelled') {
          streamError = 'Abgebrochen';
          clearStoredJobId();
          activeJobIdRef.current = '';
          try {
            window.__odiumPendingLiveWrite = null;
          } catch (_) {
            /* ignore */
          }
          const partial = String(data?.reply || '').trim();
          if (partial) {
            patchStream((m) => ({
              ...m,
              content: partial,
              streaming: false,
              pendingJob: false,
              streamId: undefined,
            }));
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';
        for (const chunk of chunks) {
          const lines = chunk.split('\n');
          let event = 'message';
          const dataLines = [];
          for (const line of lines) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
          }
          if (!dataLines.length) continue;
          let data = null;
          try {
            data = JSON.parse(dataLines.join('\n'));
          } catch (_) {
            continue;
          }
          handleEvent(event, data);
        }
      }

      if (streamError && streamError !== 'Abgebrochen') {
        setError(streamError);
        patchStream((m) => ({
          ...m,
          content: m.content || `Fehler: ${streamError}`,
          isError: !m.content,
          streaming: false,
          pendingJob: false,
          streamId: undefined,
        }));
      } else if (!gotDone && streamError === 'Abgebrochen') {
        patchStream((m) => ({
          ...m,
          streaming: false,
          pendingJob: false,
          streamId: undefined,
        }));
      } else if (!gotDone) {
        const still = activeJobIdRef.current || readStoredJobId();
        if (still) {
          patchStream((m) => ({
            ...m,
            streaming: false,
            pendingJob: true,
            jobId: still,
            streamId: undefined,
          }));
        }
      }
    } catch (err) {
      const aborted = err?.name === 'AbortError' || ctrl?.signal?.aborted;
      if (aborted && (activeJobIdRef.current || readStoredJobId())) {
        patchStream((m) => ({
          ...m,
          streaming: false,
          pendingJob: true,
          streamId: undefined,
        }));
      } else if (!aborted) {
        clearStoredJobId();
        activeJobIdRef.current = '';
        setError(err.message || 'Fortsetzen fehlgeschlagen');
        patchStream((m) => ({
          ...m,
          streaming: false,
          pendingJob: false,
          streamId: undefined,
          isError: !m.content,
          content: m.content || `Fehler: ${err.message || 'Fortsetzen fehlgeschlagen'}`,
        }));
      }
    } finally {
      abortRef.current = null;
      setSending(false);
      setPhase('');
      setPhaseMessage('');
    }
  };

  useEffect(() => {
    if (resumeStartedRef.current) return undefined;
    const jobId =
      readStoredJobId() ||
      messages.find((m) => m.pendingJob && m.jobId)?.jobId ||
      '';
    if (!jobId) return undefined;
    resumeStartedRef.current = true;
    resumeJob(jobId);
    return undefined;
    // nur einmal beim Mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = (e) => {
    if (fileSuggest?.open && fileSuggest.items?.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFileSuggest((prev) => {
          if (!prev?.items?.length) return prev;
          const next = (prev.index + 1) % prev.items.length;
          return { ...prev, index: next };
        });
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFileSuggest((prev) => {
          if (!prev?.items?.length) return prev;
          const next = (prev.index - 1 + prev.items.length) % prev.items.length;
          return { ...prev, index: next };
        });
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const item = fileSuggest.items[fileSuggest.index];
        if (item) acceptFileSuggestion(item);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setFileSuggest(null);
        return;
      }
    }
    if (e.key !== 'Enter' || e.shiftKey) return;
    // Mobile: Enter = Zeilenumbruch, Senden über Button.
    const coarse =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(pointer: coarse)').matches;
    if (coarse) return;
    e.preventDefault();
    send();
  };

  const openSidebar = () => {
    window.dispatchEvent(new CustomEvent('odium-open-sidebar'));
  };

  return (
    <div
      ref={rootRef}
      className="ai-chat-root h-full min-h-0 flex flex-col bg-neutral-50 dark:bg-neutral-950"
    >
      <div
        className="flex-shrink-0 border-b border-neutral-200 dark:border-neutral-800 px-2 sm:px-4 bg-white dark:bg-neutral-900"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        {/* Zeile 1: Titel + Modus + Mehr — unter lg kompakt, Sekundäres im Overflow */}
        <div className="flex items-center justify-between gap-1 sm:gap-2 lg:gap-3 min-h-[3rem] sm:min-h-[63px] sm:h-[63px]">
          <div className="min-w-0 flex items-center gap-1 sm:gap-2.5">
            <button
              type="button"
              onClick={openSidebar}
              className="lg:hidden p-2.5 -ml-0.5 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors touch-manipulation flex-shrink-0 min-w-[2.75rem] min-h-[2.75rem] inline-flex items-center justify-center"
              aria-label="Menü öffnen"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="hidden sm:flex w-8 h-8 rounded-lg bg-neutral-900 text-white dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-1 dark:ring-emerald-500/30 items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4" />
            </div>
            <div className="min-w-0 max-w-[5.5rem] sm:max-w-[9rem] md:max-w-[14rem] lg:max-w-none">
              <h1 className="text-sm sm:text-base font-bold text-neutral-900 dark:text-white truncate leading-tight">
                {chatMode === 'agent' ? 'KI-Agent' : 'KI-Chat'}
              </h1>
              <p className="hidden md:block text-[11px] text-neutral-500 dark:text-neutral-500 truncate leading-tight mt-0.5">
                {status.loading
                  ? 'Prüfe KI…'
                  : status.online
                    ? [
                        (chatBackend || status.backend)?.label,
                        friendlyModelLabel(activeModel || (chatBackend || status.backend)?.model),
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'Online'
                    : status.error || 'KI offline'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-0.5 sm:gap-1.5 lg:gap-2 flex-shrink-0">
            <div
              className="inline-flex items-center rounded-lg border border-neutral-200 bg-neutral-100/80 p-0.5 dark:border-neutral-700 dark:bg-neutral-950"
              role="group"
              aria-label="Chat-Modus"
            >
              <button
                type="button"
                disabled={sending}
                onClick={() => {
                  setChatMode('chat');
                  try {
                    localStorage.setItem(MODE_STORAGE_KEY, 'chat');
                  } catch (_) {
                    /* ignore */
                  }
                }}
                className={`inline-flex items-center justify-center gap-1 rounded-md min-w-[2.5rem] min-h-[2.5rem] px-2 py-1.5 lg:min-w-0 lg:min-h-0 lg:py-1 text-[11px] font-medium transition-colors disabled:opacity-50 touch-manipulation ${
                  chatMode === 'chat'
                    ? 'bg-white text-neutral-900 shadow-sm dark:bg-neutral-800 dark:text-neutral-50'
                    : 'text-neutral-500 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-200'
                }`}
                title="Normaler Chat"
                aria-label="Chat"
                aria-pressed={chatMode === 'chat'}
              >
                <MessageSquare className="w-4 h-4 lg:w-3 lg:h-3" />
                <span className="hidden lg:inline">Chat</span>
              </button>
              <button
                type="button"
                disabled={sending}
                onClick={() => {
                  setChatMode('agent');
                  try {
                    localStorage.setItem(MODE_STORAGE_KEY, 'agent');
                  } catch (_) {
                    /* ignore */
                  }
                }}
                className={`inline-flex items-center justify-center gap-1 rounded-md min-w-[2.5rem] min-h-[2.5rem] px-2 py-1.5 lg:min-w-0 lg:min-h-0 lg:py-1 text-[11px] font-medium transition-colors disabled:opacity-50 touch-manipulation ${
                  chatMode === 'agent'
                    ? 'bg-white text-neutral-900 shadow-sm dark:bg-neutral-800 dark:text-emerald-300'
                    : 'text-neutral-500 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-200'
                }`}
                title="Agent mit Tools (Suche, Seiten, Bild)"
                aria-label="Agent"
                aria-pressed={chatMode === 'agent'}
              >
                <Wrench className="w-4 h-4 lg:w-3 lg:h-3" />
                <span className="hidden lg:inline">Agent</span>
              </button>
            </div>

            {/* Desktop (lg+): Backend + Modell sichtbar */}
            <label className="hidden lg:block min-w-0 w-[7.5rem]">
              <span className="sr-only">KI-Backend</span>
              <SelectField
                value={selectedBackend}
                disabled={sending}
                onChange={(e) => {
                  const next = String(e.target.value || 'auto').trim().toLowerCase();
                  setSelectedBackend(next);
                  setChatBackend(null);
                  try {
                    localStorage.setItem(BACKEND_STORAGE_KEY, next);
                  } catch (_) {
                    /* ignore */
                  }
                }}
                className="input-field w-full max-w-full text-xs !py-1.5 !pl-2 !pr-7 disabled:opacity-50"
                chevronSize="w-3.5 h-3.5"
                menuMinWidth={148}
                menuAlign="end"
                title="Heim / Pi / Server"
                aria-label="KI-Backend"
              >
                {BACKEND_OPTIONS.map((opt) => {
                  const live = status.backends.find((b) => b.id === opt.id);
                  const offline = opt.id !== 'auto' && live && !live.online;
                  return (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                      {offline ? ' (offline)' : ''}
                    </option>
                  );
                })}
              </SelectField>
            </label>
            {status.models.length > 0 ? (
              <label className="hidden lg:block min-w-0 w-[14rem]">
                <span className="sr-only">KI-Modell</span>
                <SelectField
                  value={activeModel}
                  disabled={sending || !status.online}
                  onChange={(e) => {
                    const next = e.target.value;
                    setSelectedModel(next);
                    try {
                      localStorage.setItem(MODEL_STORAGE_KEY, next);
                    } catch (_) {
                      /* ignore */
                    }
                  }}
                  className="input-field w-full max-w-full text-xs !py-1.5 !pl-2 !pr-7 disabled:opacity-50"
                  chevronSize="w-3.5 h-3.5"
                  menuMinWidth={260}
                  menuAlign="end"
                  title="Modell wechseln"
                  aria-label="KI-Modell"
                >
                  {status.models.map((m) => (
                    <option key={m} value={m}>
                      {friendlyModelLabel(m)}
                    </option>
                  ))}
                </SelectField>
              </label>
            ) : null}

            <span className="hidden lg:inline-flex">
              <ContextWindowMeter used={contextUsed} max={contextWindow} percent={contextPercent} />
            </span>
            <span
              className={`hidden lg:inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium border ${
                status.online
                  ? 'bg-success-50 text-success-700 border-success-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/35'
                  : 'bg-neutral-100 text-neutral-500 border-neutral-200 dark:bg-neutral-900 dark:text-neutral-400 dark:border-neutral-700'
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  status.online ? 'bg-success-500 dark:bg-emerald-400' : 'bg-neutral-400'
                }`}
              />
              {status.online ? 'Online' : 'Offline'}
            </span>

            <button
              type="button"
              onClick={() => setShowRulesPanel(true)}
              className="hidden lg:inline-flex btn-ghost !p-2 dark:text-neutral-400 dark:hover:text-neutral-100 dark:hover:bg-neutral-800 touch-manipulation"
              title="KI-Regeln anzeigen und bearbeiten"
            >
              <ScrollText className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                setMessages([]);
                setError('');
                setAttachments([]);
                setLastUsage(null);
                try {
                  sessionStorage.removeItem(CHAT_STORAGE_KEY);
                  clearStoredJobId();
                  activeJobIdRef.current = '';
                } catch (_) {
                  // ignore
                }
              }}
              className="hidden lg:inline-flex btn-ghost !p-2 dark:text-neutral-400 dark:hover:text-neutral-100 dark:hover:bg-neutral-800 touch-manipulation"
              title="Chat leeren"
              disabled={!messages.length && !attachments.length}
            >
              <Trash2 className="w-4 h-4" />
            </button>

            {/* Mobile / Tablet: Mehr-Menü (Backend, Modell, Status, Aktionen) */}
            <div className="relative lg:hidden" ref={headerMoreRef}>
              <button
                type="button"
                onClick={() => setHeaderMoreOpen((v) => !v)}
                className="p-2.5 rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 touch-manipulation min-w-[2.75rem] min-h-[2.75rem] inline-flex items-center justify-center"
                aria-label="Mehr Optionen"
                aria-expanded={headerMoreOpen}
              >
                <MoreHorizontal className="w-5 h-5" />
              </button>
              {headerMoreOpen ? (
                <div className="absolute right-0 top-full mt-1 z-40 w-[min(18rem,calc(100vw-1.5rem))] rounded-xl border border-neutral-200 bg-white p-2.5 shadow-xl dark:border-neutral-700 dark:bg-neutral-900 space-y-2">
                  <div className="flex items-center justify-between gap-2 px-0.5">
                    <span className="text-[11px] text-neutral-500">Status</span>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium">
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          status.online ? 'bg-emerald-400' : 'bg-neutral-400'
                        }`}
                      />
                      {status.online ? 'Online' : 'Offline'}
                    </span>
                  </div>
                  <ContextWindowMeter used={contextUsed} max={contextWindow} percent={contextPercent} />
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1 block">
                      Backend
                    </span>
                    <SelectField
                      value={selectedBackend}
                      disabled={sending}
                      onChange={(e) => {
                        const next = String(e.target.value || 'auto').trim().toLowerCase();
                        setSelectedBackend(next);
                        setChatBackend(null);
                        try {
                          localStorage.setItem(BACKEND_STORAGE_KEY, next);
                        } catch (_) {
                          /* ignore */
                        }
                      }}
                      className="input-field w-full text-[12px] !py-2 !pl-2.5 !pr-8"
                      chevronSize="w-3.5 h-3.5"
                      menuMinWidth={160}
                      menuAlign="end"
                      aria-label="KI-Backend"
                    >
                      {BACKEND_OPTIONS.map((opt) => {
                        const live = status.backends.find((b) => b.id === opt.id);
                        const offline = opt.id !== 'auto' && live && !live.online;
                        return (
                          <option key={opt.id} value={opt.id}>
                            {opt.label}
                            {offline ? ' (offline)' : ''}
                          </option>
                        );
                      })}
                    </SelectField>
                  </label>
                  {status.models.length > 0 ? (
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1 block">
                        Modell
                      </span>
                      <SelectField
                        value={activeModel}
                        disabled={sending || !status.online}
                        onChange={(e) => {
                          const next = e.target.value;
                          setSelectedModel(next);
                          try {
                            localStorage.setItem(MODEL_STORAGE_KEY, next);
                          } catch (_) {
                            /* ignore */
                          }
                          setHeaderMoreOpen(false);
                        }}
                        className="input-field w-full text-[12px] !py-2 !pl-2.5 !pr-8"
                        chevronSize="w-3.5 h-3.5"
                        menuMinWidth={220}
                        menuAlign="end"
                        aria-label="KI-Modell"
                      >
                        {status.models.map((m) => (
                          <option key={m} value={m}>
                            {friendlyModelLabel(m)}
                          </option>
                        ))}
                      </SelectField>
                    </label>
                  ) : null}
                  <div className="flex gap-1 pt-1 border-t border-neutral-200 dark:border-neutral-800">
                    <button
                      type="button"
                      onClick={() => {
                        setHeaderMoreOpen(false);
                        setShowRulesPanel(true);
                      }}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[12px] text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
                    >
                      <ScrollText className="w-3.5 h-3.5" />
                      Regeln
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMessages([]);
                        setError('');
                        setAttachments([]);
                        setLastUsage(null);
                        try {
                          sessionStorage.removeItem(CHAT_STORAGE_KEY);
                          clearStoredJobId();
                          activeJobIdRef.current = '';
                        } catch (_) {
                          /* ignore */
                        }
                        setHeaderMoreOpen(false);
                      }}
                      disabled={!messages.length && !attachments.length}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-[12px] text-rose-600 hover:bg-rose-500/10 disabled:opacity-40 dark:text-rose-300"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Leeren
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Agent-Tabs */}
      {chatMode === 'agent' ? (
        <div className="md:hidden flex-shrink-0 flex border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-1.5 py-1 gap-1">
          {[
            { id: 'files', label: 'Dateien', Icon: FolderTree },
            { id: 'work', label: 'Editor', Icon: Code2 },
            { id: 'chat', label: 'Chat', Icon: MessageSquare },
          ].map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setAgentMobileTab(id)}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg py-2 text-[12px] font-medium touch-manipulation ${
                agentMobileTab === id
                  ? 'bg-neutral-900 text-white dark:bg-emerald-500/20 dark:text-emerald-300'
                  : 'text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 dark:text-neutral-400'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
      ) : null}

      <div
        ref={agentLayoutRef}
        className={`flex-1 min-h-0 flex ${
          chatMode === 'agent' ? 'flex-col md:flex-row overflow-hidden' : 'flex-col'
        }`}
      >
        {chatMode === 'agent' ? (
          <div
            className={`min-h-0 min-w-0 ${
              agentMobileTab === 'files' || agentMobileTab === 'work'
                ? 'flex flex-1'
                : 'hidden'
            } md:flex md:flex-1`}
          >
            <AgentWorkspace
              projectId={projectId}
              onProjectIdChange={(id) => {
                setProjectId(id);
                setPinnedPaths([]);
                setCodeRefs([]);
                try {
                  if (id) localStorage.setItem(PROJECT_STORAGE_KEY, id);
                  else localStorage.removeItem(PROJECT_STORAGE_KEY);
                } catch (_) {
                  /* ignore */
                }
              }}
              activeFilePath={activeFilePath}
              agentWritingPath={agentWritingPath}
              onFollowAgentFile={() => {
                const p = normalizeAgentPath(agentWritingPathRef.current || agentWritingPath);
                if (!p) return;
                followAgentFileRef.current = true;
                setActiveFilePath(p);
                if (isNarrow) setAgentMobileTab('work');
              }}
              onActiveFilePathChange={(path) => {
                selectUserFile(path);
                if (path && isNarrow) setAgentMobileTab('work');
              }}
              pinnedPaths={pinnedPaths}
              onPinFile={togglePinPath}
              treeRefresh={workspaceTick}
              previewRefresh={previewTick}
              mobilePanel={
                isNarrow
                  ? agentMobileTab === 'files'
                    ? 'files'
                    : agentMobileTab === 'work'
                      ? 'editor'
                      : null
                  : null
              }
            />
          </div>
        ) : null}

        <div
          className={`relative min-h-0 flex flex-col ${
            chatMode === 'agent'
              ? `${
                  agentMobileTab === 'chat' ? 'flex' : 'hidden'
                } flex-1 md:flex-none md:relative md:flex-shrink-0 md:border-l border-neutral-200 dark:border-neutral-800`
              : 'flex-1'
          }`}
          style={
            chatMode === 'agent' && !isNarrow
              ? { width: agentChatWidth, minWidth: AGENT_CHAT_WIDTH_MIN, maxWidth: '72vw' }
              : undefined
          }
          onDragEnter={onAttachDragEnter}
          onDragOver={onAttachDragOver}
          onDragLeave={onAttachDragLeave}
          onDrop={onAttachDrop}
        >
          {canAcceptAttachDrop ? (
            <div className="pointer-events-none absolute inset-2 sm:inset-3 z-30 flex items-center justify-center rounded-2xl border-2 border-dashed border-primary-400/80 bg-primary-500/10 backdrop-blur-[1px]">
              <p className="inline-flex items-center gap-2 rounded-full bg-white/90 dark:bg-neutral-900/90 px-3 py-1.5 text-sm font-medium text-primary-700 dark:text-primary-200 shadow-sm">
                <Paperclip className="w-4 h-4" />
                Dateien hier ablegen (max. {MAX_ATTACH_FILES}, je 5 MB)
              </p>
            </div>
          ) : null}
          {chatMode === 'agent' ? (
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Chat-Leiste verbreitern oder verkleinern"
              title="Ziehen zum Anpassen der Breite"
              className="hidden md:block absolute left-0 top-0 bottom-0 z-20 w-2 -translate-x-1/2 cursor-col-resize touch-none group"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                resizingAgentChatRef.current = true;
                document.body.style.cursor = 'col-resize';
                document.body.style.userSelect = 'none';
                try {
                  e.currentTarget.setPointerCapture(e.pointerId);
                } catch (_) {
                  /* ignore */
                }
              }}
            >
              <span className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-neutral-600/40 group-hover:w-0.5 group-hover:bg-emerald-400/80 group-active:bg-emerald-400" />
            </div>
          ) : null}
      <div
        ref={scrollContainerRef}
        className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${
          chatMode === 'agent' ? 'px-2 py-2' : 'px-2.5 sm:px-4 py-3 sm:py-4'
        }`}
      >
        <div
          className={
            chatMode === 'agent'
              ? 'space-y-2'
              : 'max-w-3xl mx-auto space-y-2.5 sm:space-y-3'
          }
        >
          {!messages.length && !sending ? (
            <div
              className={`rounded-2xl border border-dashed border-neutral-200 dark:border-neutral-800 dark:bg-neutral-900/40 text-center ${
                chatMode === 'agent' ? 'px-3 py-6' : 'px-4 sm:px-5 py-8 sm:py-10'
              }`}
            >
              {chatMode === 'agent' ? (
                <Wrench className="w-6 h-6 mx-auto text-emerald-500/70 mb-2" />
              ) : (
                <Bot className="w-7 h-7 sm:w-8 sm:h-8 mx-auto text-neutral-400 dark:text-neutral-500 mb-2.5 sm:mb-3" />
              )}
              <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200">
                {chatMode === 'agent' ? 'Agent-Auftrag' : 'Starte ein Gespräch'}
              </p>
              {chatMode === 'agent' ? (
                <p className="mt-1 text-[11px] text-neutral-500">
                  {projectId
                    ? 'Beschreibe was gebaut werden soll — oder hefte links eine Datei an (+), dann weiß der Agent genau worum es geht.'
                    : 'Beim ersten Auftrag wird automatisch ein Projekt angelegt. Dateien erscheinen links im Tree.'}
                </p>
              ) : null}
            </div>
          ) : null}

          {messages.map((msg, idx) => (
            <div
              key={msg.streamId || `${msg.role}-${idx}`}
              className={`flex ${
                msg.role === 'user'
                  ? 'justify-center sm:justify-end'
                  : 'justify-center sm:justify-start'
              }`}
            >
              <div
                className={`w-full max-w-[100%] sm:max-w-[85%] space-y-1.5 min-w-0 ${
                  msg.role === 'assistant' ? 'sm:w-auto sm:min-w-[12rem]' : 'sm:w-auto'
                }`}
              >
                <div
                  className={`flex items-center gap-1.5 px-1 text-[10px] ${
                    msg.role === 'user'
                      ? 'justify-end text-neutral-400 dark:text-neutral-500'
                      : msg.isError
                        ? 'text-rose-600/80 dark:text-rose-300/80'
                        : 'text-neutral-400 dark:text-emerald-400/70'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <>
                      <Bot className="w-3 h-3 flex-shrink-0" />
                      <span className="uppercase tracking-wide flex-shrink-0">
                        {msg.isError
                          ? /abgebrochen/i.test(String(msg.content || ''))
                            ? 'Abgebrochen'
                            : 'Fehler'
                          : msg.chatMode === 'agent'
                            ? 'Agent'
                            : 'KI'}
                      </span>
                    </>
                  ) : (
                    <span className="uppercase tracking-wide flex-shrink-0">Du</span>
                  )}
                  {msg.modelLabel || msg.model ? (
                    <span
                      className="normal-case tracking-normal truncate max-w-[10rem] sm:max-w-[14rem]"
                      title={msg.model || msg.modelLabel}
                    >
                      · {msg.modelLabel || friendlyModelLabel(msg.model)}
                      {msg.fallback ? ' (Fallback)' : ''}
                    </span>
                  ) : null}
                  {formatMessageTime(msg.createdAt) ? (
                    <span className="normal-case tracking-normal tabular-nums flex-shrink-0">
                      · {formatMessageTime(msg.createdAt)}
                    </span>
                  ) : null}
                  {msg.role === 'assistant' && !msg.streaming && msg.durationMs != null ? (
                    <span
                      className="normal-case tracking-normal text-neutral-400 dark:text-emerald-400/80 tabular-nums flex-shrink-0"
                      title="Dauer bis zur Antwort"
                    >
                      · {formatThoughtDuration(msg.durationMs)}
                    </span>
                  ) : null}
                  {msg.streaming ? (
                    <button
                      type="button"
                      onClick={cancelGeneration}
                      className="ml-auto normal-case tracking-normal inline-flex items-center gap-1 rounded-full border border-rose-500/25 bg-rose-500/10 px-2 py-0.5 text-[10px] font-medium text-rose-600 transition-colors hover:bg-rose-500/20 hover:border-rose-500/40 dark:text-rose-300 dark:border-rose-400/25 dark:bg-rose-500/10 dark:hover:bg-rose-500/20"
                      title="Antwort abbrechen"
                    >
                      <StopCircle className="w-3 h-3" />
                      Stop
                    </button>
                  ) : null}
                </div>
                {msg.role === 'assistant' &&
                msg.streaming &&
                (msg.chatMode || chatMode) !== 'agent' ? (
                  <PhasePipelineStrip
                    pipeline={pipeline}
                    phase={phase}
                    phaseMessage={phaseMessage}
                    elapsedLabel={formatThoughtDuration(elapsedMs)}
                  />
                ) : null}
                {msg.role === 'assistant' &&
                (() => {
                  const isAgentMsg =
                    msg.chatMode === 'agent' ||
                    (Boolean(msg.streaming) && chatMode === 'agent');
                  const hasSteps =
                    Array.isArray(msg.agentSteps) && msg.agentSteps.length > 0;
                  const hasLiveThink =
                    Boolean(msg.streaming) && Boolean(String(msg.thinkingText || '').trim());
                  const hasSearch =
                    Boolean(msg.search?.sources?.length) ||
                    Boolean(msg.search?.queries?.length) ||
                    Boolean(msg.search?.error);
                  return isAgentMsg ? hasSteps || hasLiveThink || Boolean(msg.streaming) : hasSearch;
                })() ? (
                  <AiResearchProcess
                    projectId={projectId}
                    pipeline={
                      msg.chatMode === 'agent' ||
                      (Boolean(msg.streaming) && chatMode === 'agent')
                        ? []
                        : msg.streaming
                          ? pipeline
                          : []
                    }
                    phase={msg.streaming ? phase : 'write'}
                    phaseMessage={msg.streaming ? phaseMessage : ''}
                    agentSteps={msg.agentSteps}
                    search={msg.search}
                    thinkingText={msg.thinkingText || ''}
                    streaming={Boolean(msg.streaming)}
                    agentMode={
                      msg.chatMode === 'agent' ||
                      (Boolean(msg.streaming) && chatMode === 'agent')
                    }
                    elapsedLabel={
                      msg.streaming
                        ? formatThoughtDuration(elapsedMs)
                        : msg.durationMs != null
                          ? formatThoughtDuration(msg.durationMs)
                          : ''
                    }
                    onOpenDiff={chatMode === 'agent' ? openFileWithDiff : undefined}
                    onRevisionRejected={markAgentRevisionRejected}
                  />
                ) : null}
                {msg.role === 'assistant' && msg.agentQuestion?.question ? (
                  <div className="rounded-xl border border-sky-500/25 bg-sky-500/5 px-3 py-2.5 dark:border-sky-500/30 dark:bg-sky-500/10 space-y-2">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-700/80 dark:text-sky-300/90">
                      Agent-Frage
                    </p>
                    <p className="text-[13px] text-neutral-800 dark:text-neutral-100 whitespace-pre-wrap">
                      {msg.agentQuestion.question}
                    </p>
                    {msg.agentQuestion.answered ? (
                      <p className="text-[12px] text-sky-800 dark:text-sky-200">
                        Deine Antwort:{' '}
                        <span className="font-medium">{msg.agentQuestion.answer}</span>
                      </p>
                    ) : (
                      <>
                        {msg.agentQuestion.options?.length ? (
                          <div className="flex flex-wrap gap-1.5">
                            {msg.agentQuestion.options.map((opt) => (
                              <button
                                key={opt}
                                type="button"
                                disabled={agentAnswerBusy}
                                onClick={() => submitAgentAnswer(opt, true)}
                                className="inline-flex items-center rounded-lg border border-sky-300/80 dark:border-sky-700 bg-white dark:bg-neutral-900 px-2.5 py-1.5 text-[12px] font-medium text-sky-800 dark:text-sky-200 hover:bg-sky-50 dark:hover:bg-sky-950/40 disabled:opacity-50"
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        ) : null}
                        {msg.agentQuestion.allowFreeText !== false ? (
                          <div className="flex gap-1.5">
                            <input
                              type="text"
                              value={agentAnswerDraft}
                              onChange={(e) => setAgentAnswerDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  submitAgentAnswer(agentAnswerDraft, false);
                                }
                              }}
                              placeholder="Antwort eingeben…"
                              disabled={agentAnswerBusy}
                              className="flex-1 min-w-0 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2.5 py-1.5 text-[12px] text-neutral-800 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-sky-500/40"
                            />
                            <button
                              type="button"
                              disabled={agentAnswerBusy || !agentAnswerDraft.trim()}
                              onClick={() => submitAgentAnswer(agentAnswerDraft, false)}
                              className="inline-flex items-center rounded-lg bg-sky-600 hover:bg-sky-500 text-white px-2.5 py-1.5 text-[12px] font-medium disabled:opacity-50"
                            >
                              {agentAnswerBusy ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                'Senden'
                              )}
                            </button>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                ) : null}
                {(() => {
                  const isAgentMsg =
                    msg.chatMode === 'agent' ||
                    (Boolean(msg.streaming) && chatMode === 'agent');
                  const hasAnswerBody =
                    Boolean(String(msg.content || '').trim()) ||
                    Boolean(msg.isError) ||
                    (Array.isArray(msg.images) && msg.images.length > 0) ||
                    (Array.isArray(msg.videos) && msg.videos.length > 0) ||
                    (Array.isArray(msg.audios) && msg.audios.length > 0) ||
                    (Array.isArray(msg.pdfs) && msg.pdfs.length > 0) ||
                    (Array.isArray(msg.downloads) && msg.downloads.length > 0) ||
                    Boolean(msg.imageError) ||
                    Boolean(msg.videoError) ||
                    Boolean(msg.audioError) ||
                    Boolean(msg.pdfError) ||
                    (msg.role === 'user' &&
                      ((Array.isArray(msg.attachments) && msg.attachments.length > 0) ||
                        (Array.isArray(msg.pinnedPaths) && msg.pinnedPaths.length > 0) ||
                        (Array.isArray(msg.codeRefs) && msg.codeRefs.length > 0)));
                  // Agent: leere Bubble erst zeigen, wenn die KI wirklich etwas sagt
                  if (isAgentMsg && msg.role === 'assistant' && !hasAnswerBody) return null;
                  // Chat: Streaming-Placeholder ohne Content bleibt erlaubt
                  if (
                    msg.role === 'assistant' &&
                    !hasAnswerBody &&
                    !(msg.streaming && !isAgentMsg)
                  ) {
                    return null;
                  }
                  return (
                <div
                  className={`rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed break-words ${
                    msg.role === 'user'
                      ? 'bg-neutral-900 text-white border border-neutral-800 dark:bg-neutral-800 dark:text-neutral-50 dark:border-neutral-600/80 dark:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)]'
                      : msg.isError
                        ? 'bg-rose-50 text-rose-900 border border-rose-200/90 dark:bg-neutral-900/90 dark:text-rose-200 dark:border-rose-500/25 dark:ring-1 dark:ring-rose-500/15'
                        : 'bg-neutral-100 text-neutral-800 border border-neutral-200 dark:bg-neutral-900/90 dark:text-neutral-200 dark:border-emerald-500/20 dark:ring-1 dark:ring-emerald-500/10'
                  }`}
                >
                  {msg.role === 'user' && msg.pinnedPaths?.length ? (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {msg.pinnedPaths.map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => {
                            selectUserFile(p);
                            try {
                              window.dispatchEvent(
                                new CustomEvent('odium:agent-workspace', {
                                  detail: { tab: 'editor' },
                                })
                              );
                            } catch (_) {
                              /* ignore */
                            }
                          }}
                          className="inline-flex items-center gap-1 rounded-lg bg-sky-500/20 border border-sky-400/30 px-2 py-1 text-[11px] text-sky-100 hover:bg-sky-500/30"
                          title={`Datei öffnen: ${p}`}
                        >
                          <Pin className="w-3 h-3 flex-shrink-0 fill-sky-300/40" />
                          <span className="font-mono truncate max-w-[12rem]">{p}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {msg.role === 'user' && msg.codeRefs?.length ? (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {msg.codeRefs.map((r, idx) => {
                        const isDom = r.kind === 'dom';
                        const alias =
                          r.alias ||
                          (isDom
                            ? `@${(r.label || 'UI').replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 16) || 'UI'}`
                            : `@${(String(r.path || 'Code').split('/').pop() || 'Code')
                                .replace(/\.[^.]+$/, '')
                                .replace(/[^a-zA-Z0-9_-]/g, '')
                                .slice(0, 16) || 'Code'}`);
                        const detail = isDom
                          ? domCodeRefDetail(r)
                          : `${(r.path || 'Auswahl').split('/').pop()}${
                              r.startLine
                                ? `:${r.startLine}${
                                    r.endLine && r.endLine !== r.startLine ? `-${r.endLine}` : ''
                                  }`
                                : ''
                            }`;
                        return (
                          <button
                            key={r.id || `${r.path}-${r.startLine}-${idx}`}
                            type="button"
                            onClick={() => {
                              if (isDom) {
                                try {
                                  window.dispatchEvent(
                                    new CustomEvent('odium:agent-workspace', {
                                      detail: { tab: 'live' },
                                    })
                                  );
                                } catch (_) {
                                  /* ignore */
                                }
                                return;
                              }
                              if (r.path) selectUserFile(r.path);
                              try {
                                window.dispatchEvent(
                                  new CustomEvent('odium:agent-workspace', {
                                    detail: { tab: 'editor' },
                                  })
                                );
                              } catch (_) {
                                /* ignore */
                              }
                            }}
                            className={`inline-flex items-center gap-1.5 rounded-full border pl-2 pr-2.5 py-1 text-[11px] max-w-full ${
                              isDom
                                ? 'border-sky-400/35 bg-sky-500/20 text-sky-50 hover:bg-sky-500/30'
                                : 'border-violet-400/35 bg-violet-500/20 text-violet-50 hover:bg-violet-500/30'
                            }`}
                            title={
                              isDom
                                ? `${alias}\n${r.label || ''}\n${r.selector || ''}`
                                : `${alias}\n${r.path}:${r.startLine}-${r.endLine}`
                            }
                          >
                            {isDom ? (
                              <MousePointer2 className="w-3 h-3 flex-shrink-0 opacity-80" />
                            ) : (
                              <Code2 className="w-3 h-3 flex-shrink-0 opacity-80" />
                            )}
                            <span className="font-semibold tracking-tight">{alias}</span>
                            <span className="opacity-70 truncate max-w-[10rem]">
                              {detail}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                  {msg.role === 'user' && msg.attachments?.length ? (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {msg.attachments.map((file) => {
                        const isImg =
                          String(file.mimeType || '').startsWith('image/') ||
                          /\.(png|jpe?g|webp|gif|bmp)$/i.test(file.name || '');
                        return (
                          <span
                            key={`${file.name}-${file.size || 0}`}
                            className="inline-flex items-center gap-1 rounded-lg bg-white/10 px-2 py-1 text-[11px] text-neutral-100"
                          >
                            {isImg ? <ImagePlus className="w-3 h-3 flex-shrink-0" /> : <FileText className="w-3 h-3 flex-shrink-0" />}
                            <span className="truncate max-w-[10rem]">{file.name}</span>
                          </span>
                        );
                      })}
                    </div>
                  ) : null}
                  {msg.isError ? (
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  ) : (
                    <>
                      {msg.images?.length ? (
                        <div className="mb-2.5 space-y-2">
                          {msg.images.map((img) => (
                            <a
                              key={img.url || img.mediaId || img.publicId}
                              href={img.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-950/5 dark:bg-black/20"
                              title={img.prompt || img.name || 'KI-Bild'}
                            >
                              <img
                                src={img.url}
                                alt={img.prompt || img.name || 'KI-generiertes Bild'}
                                className="max-h-56 sm:max-h-80 w-full object-contain bg-neutral-100 dark:bg-neutral-950"
                                loading="lazy"
                              />
                              {img.prompt ? (
                                <p className="px-2.5 py-1.5 text-[10px] text-neutral-500 dark:text-neutral-400 truncate">
                                  {img.prompt}
                                </p>
                              ) : null}
                            </a>
                          ))}
                        </div>
                      ) : null}
                      {msg.audios?.length ? (
                        <div className="mb-2.5 space-y-2">
                          {msg.audios.map((audio) => (
                            <div
                              key={audio.url || audio.mediaId || audio.publicId}
                              className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-950/60 px-3 py-2.5"
                            >
                              <div className="flex items-center gap-1.5 mb-1.5 text-[11px] text-neutral-600 dark:text-neutral-300">
                                <Volume2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                                <span className="font-medium truncate min-w-0">
                                  {audio.name || 'KI-Sound'}
                                </span>
                                {audio.kind ? (
                                  <span className="text-[10px] text-neutral-400 flex-shrink-0">
                                    · {audio.kind}
                                  </span>
                                ) : null}
                              </div>
                              <audio
                                controls
                                preload="metadata"
                                src={audio.url}
                                className="w-full h-9"
                              >
                                Dein Browser unterstützt kein Audio.
                              </audio>
                              {audio.prompt ? (
                                <p className="mt-1.5 text-[10px] text-neutral-500 dark:text-neutral-400 line-clamp-2">
                                  {audio.prompt}
                                </p>
                              ) : null}
                              {audio.url ? (
                                <a
                                  href={audio.url}
                                  download={audio.name || 'sound.mp3'}
                                  className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-300 hover:underline"
                                >
                                  <Download className="w-3 h-3" />
                                  Download
                                </a>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {msg.pdfs?.length ? (
                        <div className="mb-2.5 space-y-2">
                          {msg.pdfs.map((pdf) => (
                            <div
                              key={pdf.url || pdf.mediaId || pdf.publicId}
                              className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-950/60 px-3 py-2.5"
                            >
                              <div className="flex items-center gap-1.5 mb-1.5 text-[11px] text-neutral-600 dark:text-neutral-300">
                                <FileText className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400 flex-shrink-0" />
                                <span className="font-medium truncate min-w-0">
                                  {pdf.name || 'KI-PDF'}
                                </span>
                                {pdf.provider ? (
                                  <span className="text-[10px] text-neutral-400 flex-shrink-0">
                                    · {pdf.provider}
                                  </span>
                                ) : null}
                              </div>
                              {pdf.prompt ? (
                                <p className="text-[10px] text-neutral-500 dark:text-neutral-400 line-clamp-2">
                                  {pdf.prompt}
                                </p>
                              ) : null}
                              {pdf.url ? (
                                <a
                                  href={pdf.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  download={pdf.name || 'dokument.pdf'}
                                  className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-rose-700 dark:text-rose-300 hover:underline"
                                >
                                  <Download className="w-3 h-3" />
                                  PDF öffnen / Download
                                </a>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {msg.pdfError ? (
                        <p className="mb-2 text-[12px] text-rose-600 dark:text-rose-400">
                          PDF fehlgeschlagen: {msg.pdfError}
                        </p>
                      ) : null}
                      {msg.videos?.length ? (
                        <div className="mb-2.5 space-y-2">
                          {msg.videos.map((video) => (
                            <div
                              key={video.url || video.mediaId || video.publicId}
                              className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-950/60 overflow-hidden"
                            >
                              <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1.5 text-[11px] text-neutral-600 dark:text-neutral-300">
                                <Film className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400 flex-shrink-0" />
                                <span className="font-medium truncate min-w-0">
                                  {video.name || 'KI-Video'}
                                </span>
                                {video.duration ? (
                                  <span className="text-[10px] text-neutral-400 flex-shrink-0">
                                    · {video.duration}s
                                  </span>
                                ) : null}
                              </div>
                              <video
                                controls
                                playsInline
                                preload="metadata"
                                src={video.url}
                                className="w-full max-h-72 bg-black"
                              >
                                Dein Browser unterstützt kein Video.
                              </video>
                              {video.prompt ? (
                                <p className="px-3 py-1.5 text-[10px] text-neutral-500 dark:text-neutral-400 line-clamp-2">
                                  {video.prompt}
                                </p>
                              ) : null}
                              {video.url ? (
                                <a
                                  href={video.url}
                                  download={video.name || 'video.mp4'}
                                  className="mx-3 mb-2.5 inline-flex items-center gap-1 text-[10px] text-sky-700 dark:text-sky-300 hover:underline"
                                >
                                  <Download className="w-3 h-3" />
                                  Download
                                </a>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {msg.videoError ? (
                        <div className="mb-2 text-[12px] text-rose-600 dark:text-rose-300 [&_.ai-md-a]:underline">
                          <span className="font-medium">Video: </span>
                          <AiChatMarkdown content={String(msg.videoError)} tone="assistant" />
                        </div>
                      ) : null}
                      {msg.imageError ? (
                        <div className="mb-2 text-[12px] text-rose-600 dark:text-rose-300 [&_.ai-md-a]:underline">
                          <span className="font-medium">Bild: </span>
                          <AiChatMarkdown content={String(msg.imageError)} tone="assistant" />
                        </div>
                      ) : null}
                      {msg.audioError ? (
                        <div className="mb-2 text-[12px] text-rose-600 dark:text-rose-300 [&_.ai-md-a]:underline">
                          <span className="font-medium">Sound: </span>
                          <AiChatMarkdown content={String(msg.audioError)} tone="assistant" />
                        </div>
                      ) : null}
                      {msg.streaming &&
                      !msg.content &&
                      (msg.chatMode || chatMode) !== 'agent' ? (
                        <div className="text-[12px] text-neutral-500 dark:text-neutral-400">
                          {phaseMessage || phaseLabel(phase)}
                          <span className="ai-chat-cursor ml-1" aria-hidden="true" />
                        </div>
                      ) : msg.content &&
                        !(
                          msg.videoError &&
                          String(msg.content).trim() === String(msg.videoError).trim()
                        ) ? (
                        <AiChatMarkdown
                          content={msg.content}
                          tone={msg.role === 'user' ? 'user' : 'assistant'}
                          streaming={Boolean(msg.streaming)}
                        />
                      ) : null}
                      {msg.downloads?.length ? (
                        <div className="mt-2.5 rounded-xl border border-emerald-500/25 bg-emerald-500/5 dark:bg-emerald-950/30 px-2.5 py-2">
                          <p className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300/90 mb-1.5 flex items-center gap-1">
                            <Download className="w-3 h-3" />
                            Dateien zum Download
                          </p>
                          <ul className="space-y-1">
                            {msg.downloads.map((file, fileIdx) => (
                              <li key={`${file.name}-${fileIdx}`}>
                                <button
                                  type="button"
                                  onClick={() => triggerBase64Download(file)}
                                  disabled={!file.data}
                                  className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] text-neutral-800 dark:text-neutral-100 hover:bg-emerald-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <FileText className="w-3.5 h-3.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                                  <span className="min-w-0 flex-1 truncate font-medium">{file.name}</span>
                                  <span className="text-[10px] text-neutral-500 tabular-nums flex-shrink-0">
                                    {file.data ? formatDownloadSize(file.size) : 'neu erzeugen'}
                                  </span>
                                  <Download className="w-3.5 h-3.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
                  );
                })()}
                {!msg.streaming && !msg.isError && msg.content ? (
                  <div className="flex flex-wrap items-center gap-1 px-0.5 -mt-0.5">
                    <button
                      type="button"
                      onClick={() => copyMessageText(msg.content)}
                      className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[10px] text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:text-neutral-500 dark:hover:text-neutral-200 dark:hover:bg-neutral-800/80"
                      title="Kopieren"
                    >
                      <Copy className="w-3 h-3" />
                      <span className="hidden sm:inline">Kopieren</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleReadAloud(msg, idx)}
                      disabled={speakBusyKey != null && speakBusyKey !== `msg-${idx}-${msg.createdAt || 0}`}
                      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[10px] transition-colors disabled:opacity-50 ${
                        speakingKey === `msg-${idx}-${msg.createdAt || 0}` ||
                        speakBusyKey === `msg-${idx}-${msg.createdAt || 0}`
                          ? 'text-emerald-700 bg-emerald-500/10 dark:text-emerald-300 dark:bg-emerald-500/15'
                          : 'text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:text-neutral-500 dark:hover:text-neutral-200 dark:hover:bg-neutral-800/80'
                      }`}
                      title={
                        speakingKey === `msg-${idx}-${msg.createdAt || 0}`
                          ? 'Vorlesen stoppen'
                          : 'Mit KI-Stimme vorlesen'
                      }
                    >
                      {speakBusyKey === `msg-${idx}-${msg.createdAt || 0}` &&
                      speakingKey !== `msg-${idx}-${msg.createdAt || 0}` ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : speakingKey === `msg-${idx}-${msg.createdAt || 0}` ? (
                        <Square className="w-3 h-3" />
                      ) : (
                        <Volume2 className="w-3 h-3" />
                      )}
                      <span className="hidden sm:inline">
                        {speakingKey === `msg-${idx}-${msg.createdAt || 0}`
                          ? 'Stop'
                          : speakBusyKey === `msg-${idx}-${msg.createdAt || 0}`
                            ? 'Lädt…'
                            : 'Vorlesen'}
                      </span>
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ))}

          {sending && !messages.some((m) => m.streaming) ? (
            <div className="flex justify-center sm:justify-start">
              <div className="inline-flex flex-wrap items-center gap-x-2.5 gap-y-1.5 rounded-2xl px-3.5 py-2.5 text-[12px] text-neutral-500 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-emerald-500/20 w-full sm:w-auto max-w-full sm:max-w-none">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-500 flex-shrink-0" />
                <span className="min-w-0 truncate">{phaseMessage || phaseLabel(phase) || 'Denkt nach…'}</span>
                <span
                  className="tabular-nums text-emerald-600 dark:text-emerald-400 flex-shrink-0"
                  title="Bisherige Nachdenkzeit"
                >
                  {formatThoughtDuration(elapsedMs)}
                </span>
                <button
                  type="button"
                  onClick={cancelGeneration}
                  className="inline-flex items-center gap-1 rounded-full border border-rose-500/25 bg-rose-500/10 px-2.5 py-1 text-[11px] font-medium text-rose-600 transition-all hover:bg-rose-500/20 hover:border-rose-500/40 active:scale-[0.98] dark:text-rose-300 dark:border-rose-400/25 dark:bg-rose-500/10 dark:hover:bg-rose-500/20 touch-manipulation"
                  title="Antwort abbrechen"
                >
                  <StopCircle className="w-3.5 h-3.5" />
                  Stop
                </button>
              </div>
            </div>
          ) : null}
          <div ref={bottomRef} />
        </div>
      </div>

      <div className="flex-shrink-0 border-t border-neutral-200 dark:border-neutral-800 px-2.5 sm:px-3 lg:px-4 py-2.5 bg-white dark:bg-neutral-950 pb-[max(0.625rem,env(safe-area-inset-bottom))] lg:min-h-[var(--odium-shell-footer-height,0px)] lg:box-border flex flex-col justify-end">
        <div className="max-w-3xl mx-auto w-full space-y-2">
          {(chatBackend?.fallback || status.backend?.fallback) && (
            <p className="text-[11px] text-amber-700 dark:text-amber-300 px-0.5">
              {(chatBackend || status.backend)?.notice ||
                'Primäre KI nicht erreichbar — Fallback aktiv'}
            </p>
          )}
          {selectedBackend !== 'auto' && !status.online && status.backends.length > 0 ? (
            <p className="text-[11px] text-rose-600 dark:text-rose-300 px-0.5">
              {friendlyBackendLabel(selectedBackend, status.backends)} ist offline
              {status.backends.find((b) => b.id === selectedBackend)?.error
                ? `: ${status.backends.find((b) => b.id === selectedBackend).error}`
                : ' — Auto wählen oder ein anderes Backend.'}
            </p>
          ) : null}
          {error ? (
            <p className="text-[11px] text-rose-600 dark:text-rose-300 px-0.5">{error}</p>
          ) : null}

          {chatMode === 'agent' && pinnedPaths.length ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wide text-sky-600 dark:text-sky-400/90 flex items-center gap-1 mr-0.5">
                <Pin className="w-3 h-3" />
                Bezug
              </span>
              {pinnedPaths.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => selectUserFile(p)}
                  className="inline-flex items-center gap-1 rounded-lg border border-sky-500/30 bg-sky-500/10 pl-2 pr-1 py-1 text-[11px] text-sky-800 dark:text-sky-200 max-w-full"
                  title={p}
                >
                  <FileText className="w-3 h-3 flex-shrink-0 opacity-70" />
                  <span className="font-mono truncate max-w-[9rem] sm:max-w-[12rem]">
                    {p.split('/').pop() || p}
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePinPath(p);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        togglePinPath(p);
                      }
                    }}
                    className="rounded-full p-0.5 text-sky-600/70 hover:text-sky-900 dark:text-sky-300/70 dark:hover:text-sky-100"
                    title="Lösen"
                  >
                    <X className="w-3 h-3" />
                  </span>
                </button>
              ))}
              <button
                type="button"
                onClick={clearPinnedPaths}
                className="text-[10px] text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200 px-1"
              >
                alle lösen
              </button>
            </div>
          ) : null}

          {chatMode === 'agent' && codeRefs.length ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wide text-violet-600 dark:text-violet-400/90 flex items-center gap-1 mr-0.5">
                <Code2 className="w-3 h-3" />
                Mentions
              </span>
              {codeRefs.map((r) => {
                const isDom = r.kind === 'dom';
                const alias =
                  r.alias ||
                  (isDom ? `@${r.label || 'UI'}` : `@${(r.path || 'Code').split('/').pop()}`);
                const detail = isDom
                  ? domCodeRefDetail(r)
                  : `${(r.path || 'Auswahl').split('/').pop()}:${r.startLine}${
                      r.endLine !== r.startLine ? `-${r.endLine}` : ''
                    }`;
                return (
                  <div
                    key={r.id}
                    className={`inline-flex items-center gap-1 rounded-full border pl-2 pr-1 py-1 text-[11px] max-w-full ${
                      isDom
                        ? 'border-sky-500/30 bg-sky-500/10 text-sky-800 dark:text-sky-200'
                        : 'border-violet-500/30 bg-violet-500/10 text-violet-800 dark:text-violet-200'
                    }`}
                    title={
                      isDom
                        ? `${alias}\n${r.label || ''}\n${r.selector || ''}\n${String(r.visibleText || r.text || '').slice(0, 200)}`
                        : `${alias}\n${r.path}:${r.startLine}-${r.endLine}\n${String(r.text || '').slice(0, 200)}`
                    }
                  >
                    {isDom ? (
                      <MousePointer2 className="w-3 h-3 flex-shrink-0 opacity-70" />
                    ) : (
                      <Code2 className="w-3 h-3 flex-shrink-0 opacity-70" />
                    )}
                    <button
                      type="button"
                      onClick={() => insertAliasIntoInput(alias)}
                      className="font-semibold tracking-tight hover:underline"
                      title="Alias in die Nachricht einfügen"
                    >
                      {alias}
                    </button>
                    <span className="opacity-60 truncate max-w-[10rem]">{detail}</span>
                    <button
                      type="button"
                      onClick={() => removeCodeRef(r.id)}
                      className={`rounded-full p-0.5 ${
                        isDom
                          ? 'text-sky-600/70 hover:text-sky-900 dark:text-sky-300/70 dark:hover:text-sky-100'
                          : 'text-violet-600/70 hover:text-violet-900 dark:text-violet-300/70 dark:hover:text-violet-100'
                      }`}
                      title="Entfernen"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                onClick={clearCodeRefs}
                className="text-[10px] text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200 px-1"
              >
                alle lösen
              </button>
            </div>
          ) : null}

          {attachments.length ? (
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {attachments.map((file, idx) => (
                <div
                  key={`${file.name}-${idx}`}
                  className="relative group inline-flex items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50 pl-1.5 pr-1.5 sm:pr-2 py-1.5 text-[11px] text-neutral-700 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 max-w-full"
                >
                  {isImageAttachment(file) ? (
                    <img
                      src={attachmentPreviewUrl(file)}
                      alt={file.name}
                      className="h-9 w-9 rounded-lg object-cover border border-neutral-200 dark:border-neutral-700 flex-shrink-0"
                    />
                  ) : (
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-200/70 dark:bg-neutral-800 flex-shrink-0">
                      <FileText className="w-3.5 h-3.5" />
                    </span>
                  )}
                  <span className="truncate max-w-[6.5rem] sm:max-w-[8.5rem]">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(idx)}
                    className="rounded-full p-1.5 text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-100 touch-manipulation"
                    title="Entfernen"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          <div className="relative rounded-2xl border border-neutral-200 bg-neutral-50/80 p-1.5 sm:p-2 dark:border-neutral-800 dark:bg-neutral-900/60 focus-within:border-neutral-300 dark:focus-within:border-neutral-600 transition-colors">
            {fileSuggest?.open && fileSuggest.items?.length ? (
              <div
                className="absolute left-1 right-1 bottom-full mb-1.5 z-40 max-h-52 overflow-y-auto rounded-xl border border-neutral-200 bg-white shadow-lg dark:border-neutral-700 dark:bg-neutral-900"
                role="listbox"
                aria-label="Datei-Vorschläge"
              >
                <p className="px-2.5 pt-2 pb-1 text-[10px] font-medium uppercase tracking-wide text-neutral-400">
                  Projektdatei · Enter übernimmt Pfad
                </p>
                {fileSuggest.items.map((file, idx) => {
                  const active = idx === fileSuggest.index;
                  return (
                    <button
                      key={file.path}
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={`w-full flex items-start gap-2 px-2.5 py-1.5 text-left transition-colors ${
                        active
                          ? 'bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200'
                          : 'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800'
                      }`}
                      onMouseDown={(ev) => {
                        ev.preventDefault();
                        acceptFileSuggestion(file);
                      }}
                      onMouseEnter={() =>
                        setFileSuggest((prev) => (prev ? { ...prev, index: idx } : prev))
                      }
                    >
                      <FolderTree className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 opacity-60" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[12px] font-medium truncate">{file.name}</span>
                        <span className="block text-[10px] text-neutral-500 dark:text-neutral-400 truncate font-mono">
                          @{file.path}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}
            <textarea
              ref={inputRef}
              rows={2}
              value={input}
              onChange={(e) => {
                const next = e.target.value;
                setInput(next);
                updateFileSuggestFromInput(next, e.target.selectionStart);
              }}
              onSelect={(e) => {
                if (chatMode === 'agent') {
                  updateFileSuggestFromInput(e.target.value, e.target.selectionStart);
                }
              }}
              onClick={(e) => {
                if (chatMode === 'agent') {
                  updateFileSuggestFromInput(e.target.value, e.target.selectionStart);
                }
              }}
              onBlur={() => {
                // kurz verzögern, damit Klick auf Vorschlag noch greift
                window.setTimeout(() => setFileSuggest(null), 120);
              }}
              onKeyDown={onKeyDown}
              onPaste={(e) => {
                const files = Array.from(e.clipboardData?.files || []);
                if (files.length) {
                  e.preventDefault();
                  addFiles(files);
                }
              }}
              disabled={!status.online}
              placeholder={
                !status.online
                  ? 'KI offline'
                  : sending
                    ? 'Weiter tippen…'
                    : chatMode === 'agent'
                      ? codeRefs.length
                        ? `Auftrag zu ${
                            codeRefs.some((r) => r.kind === 'dom') ? 'UI/Code' : 'Markierung'
                          } (${codeRefs
                            .map((r) =>
                              r.alias
                                ? `${r.alias}${
                                    r.kind === 'dom' && (r.visibleText || r.label)
                                      ? ` ${r.visibleText || r.label}`
                                      : ''
                                  }`
                                : r.kind === 'dom'
                                  ? r.visibleText || r.label || r.selector || 'Element'
                                  : `${(r.path || '').split('/').pop()}:${r.startLine}`
                            )
                            .join(', ')})…`
                        : pinnedPaths.length
                          ? `Auftrag zu ${pinnedPaths.map((p) => p.split('/').pop()).join(', ')}…`
                          : 'Auftrag… — Live Select / Code markieren / @Datei'
                      : 'Nachricht… — Dateien per Drag & Drop, Einfügen oder 📎'
              }
              enterKeyHint="enter"
              className="w-full resize-none min-h-[2.75rem] sm:min-h-[2.5rem] max-h-32 sm:max-h-40 bg-transparent border-0 outline-none ring-0 focus:ring-0 px-2.5 sm:px-2 py-2 sm:py-1.5 text-[16px] sm:text-[13px] text-neutral-900 placeholder:text-neutral-400 dark:text-neutral-100 dark:placeholder:text-neutral-500"
            />

            <div className="flex items-center gap-2 pt-1.5 px-0.5">
              <div className="flex items-center gap-1 min-w-0">
                {status.webSearchEnabled && chatMode !== 'agent' ? (
                  <button
                    type="button"
                    onClick={() => setWebSearch((v) => !v)}
                    className={`inline-flex items-center justify-center h-9 w-9 rounded-lg border transition-colors touch-manipulation ${
                      webSearch
                        ? 'bg-neutral-900 text-white border-neutral-900 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/40'
                        : 'bg-transparent text-neutral-500 border-transparent hover:bg-neutral-100 hover:text-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200'
                    }`}
                    title={
                      webSearch
                        ? 'Websuche an — vor jeder Antwort'
                        : 'Websuche aus (Standard)'
                    }
                    aria-label={webSearch ? 'Websuche an' : 'Websuche aus'}
                    aria-pressed={webSearch}
                  >
                    <Globe className="w-4 h-4" />
                  </button>
                ) : null}

                {chatMode === 'agent' && activeFilePath ? (
                  <button
                    type="button"
                    onClick={() => togglePinPath(activeFilePath)}
                    className={`inline-flex items-center justify-center gap-1.5 h-9 max-w-[9rem] rounded-lg border px-2 transition-colors touch-manipulation ${
                      pinnedPaths.includes(activeFilePath)
                        ? 'bg-sky-500/15 text-sky-700 border-sky-500/35 dark:text-sky-300 dark:border-sky-500/40'
                        : 'bg-transparent text-neutral-500 border-transparent hover:bg-neutral-100 hover:text-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200'
                    }`}
                    title={
                      pinnedPaths.includes(activeFilePath)
                        ? `${activeFilePath} vom Chat lösen`
                        : `${activeFilePath} im Chat anheften`
                    }
                    aria-label={
                      pinnedPaths.includes(activeFilePath)
                        ? 'Datei vom Chat lösen'
                        : 'Datei im Chat anheften'
                    }
                    aria-pressed={pinnedPaths.includes(activeFilePath)}
                  >
                    <Pin
                      className={`w-3.5 h-3.5 flex-shrink-0 ${
                        pinnedPaths.includes(activeFilePath) ? 'fill-sky-400/40' : ''
                      }`}
                    />
                    <span className="hidden sm:inline truncate text-[11px] font-mono">
                      {activeFilePath.split('/').pop()}
                    </span>
                  </button>
                ) : null}

                <div className="relative" ref={attachMenuRef}>
                  <button
                    type="button"
                    onClick={() => setAttachMenuOpen((v) => !v)}
                    disabled={!status.online || sending || attachments.length >= MAX_ATTACH_FILES}
                    className={`inline-flex items-center justify-center h-9 w-9 rounded-lg border transition-colors touch-manipulation disabled:opacity-50 ${
                      attachMenuOpen
                        ? 'bg-neutral-900 text-white border-neutral-900 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/40'
                        : 'bg-transparent text-neutral-500 border-transparent hover:bg-neutral-100 hover:text-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200'
                    }`}
                    title="Datei oder Bild anhängen"
                    aria-label="Anhang"
                    aria-expanded={attachMenuOpen}
                    aria-haspopup="menu"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                  {attachMenuOpen ? (
                    <div
                      role="menu"
                      className="absolute bottom-full left-0 mb-1.5 z-30 min-w-[11rem] rounded-xl border border-neutral-200 bg-white py-1 shadow-lg dark:border-neutral-700 dark:bg-neutral-900"
                    >
                      <button
                        type="button"
                        role="menuitem"
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-[12px] text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800"
                        onClick={() => {
                          setAttachMenuOpen(false);
                          fileInputRef.current?.click();
                        }}
                      >
                        <Paperclip className="w-3.5 h-3.5 text-neutral-400" />
                        Datei wählen…
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-[12px] text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800"
                        onClick={() => {
                          setAttachMenuOpen(false);
                          imageInputRef.current?.click();
                        }}
                      >
                        <ImagePlus className="w-3.5 h-3.5 text-neutral-400" />
                        Bild wählen…
                      </button>
                    </div>
                  ) : null}
                </div>

                <input
                  ref={imageInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  accept="image/*,.png,.jpg,.jpeg,.webp,.gif,.bmp"
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  accept=".txt,.md,.csv,.tsv,.json,.html,.htm,.xml,.log,.yml,.yaml,.js,.jsx,.ts,.tsx,.css,.sql,.py,.sh,.ps1,image/*,.png,.jpg,.jpeg,.webp,.gif,.bmp"
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </div>

              <div className="ml-auto flex items-center gap-1.5 flex-shrink-0">
                {promptRewriteUndo ? (
                  <button
                    type="button"
                    onClick={undoPromptRewrite}
                    disabled={sending || rewritingPrompt}
                    className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-300 dark:hover:bg-neutral-900 disabled:opacity-50 touch-manipulation"
                    title="Umschreibung rückgängig"
                    aria-label="Umschreibung rückgängig"
                  >
                    <Undo2 className="w-3.5 h-3.5" />
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={rewriteComposerPrompt}
                  disabled={
                    !status.online || sending || rewritingPrompt || !String(input || '').trim()
                  }
                  className="inline-flex items-center justify-center gap-1.5 h-9 rounded-lg px-2.5 text-[11px] font-medium border border-violet-500/30 bg-violet-500/10 text-violet-800 hover:bg-violet-500/20 dark:text-violet-200 dark:border-violet-500/35 dark:hover:bg-violet-500/20 disabled:opacity-50 touch-manipulation"
                  title="Prompt umschreiben — klarer für die KI (vor dem Senden)"
                >
                  {rewritingPrompt ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Wand2 className="w-3.5 h-3.5" />
                  )}
                  <span className="hidden sm:inline">
                    {rewritingPrompt ? 'Schreibt um…' : 'Verbessern'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={sending ? cancelGeneration : send}
                  disabled={
                    !status.online ||
                    rewritingPrompt ||
                    (!sending &&
                      !input.trim() &&
                      !attachments.length &&
                      !(chatMode === 'agent' && pinnedPaths.length) &&
                      !(chatMode === 'agent' && codeRefs.length))
                  }
                  className={`h-9 w-9 inline-flex items-center justify-center rounded-lg transition-all disabled:opacity-50 active:scale-[0.97] touch-manipulation ${
                    sending
                      ? 'border border-rose-500/30 bg-rose-500/15 text-rose-600 hover:bg-rose-500/25 hover:border-rose-500/50 dark:text-rose-300 dark:border-rose-400/30 dark:bg-rose-500/15 dark:hover:bg-rose-500/25'
                      : 'btn-primary'
                  }`}
                  title={sending ? 'Stop' : 'Senden'}
                  aria-label={sending ? 'Stop' : 'Senden'}
                >
                  {sending ? <StopCircle className="w-5 h-5" strokeWidth={2} /> : <Send className="w-5 h-5" strokeWidth={2} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
      </div>
      <AiChatRulesPanel open={showRulesPanel} onClose={() => setShowRulesPanel(false)} />
    </div>
  );
};

export default AiChat;
